import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { notify } from "../ui/notify";
import ScrollDateInput from "../calendar/ScrollDateInput";
import { triggerHappyWave } from "../../utils/antdInsetWave";
import { PATH } from "../../redux/utils";
import {
  actionTribunalActuacionesGet,
  actionTribunalActuacionCreate,
  actionTribunalActuacionUpdate,
  actionTribunalActuacionDelete,
  actionTribunalActuacionUploadDocumento,
  actionTribunalActuacionDocumentoDelete,
  actionTribunalAudienciaGet,
  actionTribunalAudienciaUpsert,
  actionTribunalAudienciaDelete,
} from "../../redux/actions/tribunal/tribunal";
import {
  actionGetFormasTerminacion,
  actionGetTerminacion,
  actionSaveTerminacion,
  actionSaveConstanciaTerminacion,
} from "../../redux/actions/terminacion/terminacion";
import EsquemaProcedimiento from "./EsquemaProcedimiento";
import { ESQ, ESQG } from "./procedimientoEtapas";
import "./TabExpedienteActuaciones.css";

// Tipo interno usado para "cargar" documentos al registrar lo sucedido de una audiencia (kind 2):
// se guarda como una actuación real (para reusar el flujo de documentos) pero no se lista aparte;
// sus documentos se adjuntan al evento virtual "Audiencia celebrada — …" que ya representa esa audiencia.
const AUDIENCIA_DOC_PREFIX = "Audiencia celebrada — ";

// ── Catálogo LFT ────────────────────────────────────────────────────────
// kind: 0 normal · 1 señalamiento (agenda) · 2 audiencia (lo sucedido)
const CAT = [
  { g: "Fase escrita" },
  { n: "Presentación de demanda", autorFijo: "act", term: 0, fund: "Arts. 871-872 LFT" },
  { n: "Prevención para subsanar la demanda", autorFijo: "trib", term: 3, fund: "Art. 873 LFT" },
  { n: "Auto de admisión de demanda", autorFijo: "trib", term: 0, fund: "Art. 873 LFT · dentro de 3 días" },
  { n: "Emplazamiento", autorFijo: "trib", term: 15, fund: "Art. 873-A LFT" },
  { n: "Contestación de demanda", autorFijo: "nos", term: 0, fund: "Art. 873-A LFT" },
  { n: "Reconvención", autorFijo: "nos", term: 0, fund: "Art. 873-A LFT" },
  { n: "Contestación a la reconvención", autorFijo: "act", term: 15, fund: "Art. 873-A LFT" },
  { n: "Réplica", autorFijo: "act", term: 8, fund: "Art. 873-B LFT" },
  { n: "Contrarréplica", autorFijo: "nos", term: 5, fund: "Art. 873-C LFT" },
  { n: "Vista con pruebas de la contrarréplica", autorFijo: "act", term: 3, fund: "Art. 873-C LFT" },
  { n: "Auto de integración de la litis", autorFijo: "trib", term: 0, fund: "Art. 873-C LFT" },
  { g: "Audiencias" },
  { n: "Audiencia", kind: 1, term: 5, fund: "Arts. 873-C y 873-H LFT · primero se agenda, después se registra su resultado" },
  { g: "Incidentes y trámite" },
  { n: "Incidente", autorFijo: null, term: 0, fund: "Arts. 762-765 LFT" },
  { n: "Resolución incidental", autorFijo: "trib", term: 0, fund: "Art. 763 LFT" },
  { n: "Promoción simple", autorFijo: null, term: 0, fund: "" },
  { n: "Desahogo de vista", autorFijo: null, term: 3, fund: "Término judicial" },
  { n: "Requerimiento o prevención", autorFijo: "trib", term: 3, fund: "" },
  { n: "Auto o acuerdo", autorFijo: "trib", term: 0, fund: "" },
  { n: "Ofrecimiento o perfeccionamiento de pruebas", autorFijo: null, term: 0, fund: "Art. 873-A LFT" },
  { n: "Auto de admisión de pruebas", autorFijo: "trib", term: 0, fund: "Art. 873-G LFT" },
  { n: "Alegatos", autorFijo: null, term: 0, fund: "Art. 884 LFT" },
  { g: "Resolución, amparo y ejecución" },
  { n: "Sentencia / Laudo", autorFijo: "trib", term: 15, fund: "Arts. 885-891 LFT · plazo para amparo" },
  { n: "Demanda de amparo directo", autorFijo: null, term: 0, fund: "Ley de Amparo, arts. 17 y 170" },
  { n: "Demanda de amparo indirecto", autorFijo: null, term: 0, fund: "Ley de Amparo" },
  { n: "Ejecutoria de amparo", autorFijo: "trib", term: 0, fund: "Ley de Amparo" },
  { n: "Escrito de cumplimiento", autorFijo: null, term: 0, fund: "Arts. 940-949 LFT" },
  { n: "Auto de cumplimiento y archivo", autorFijo: "trib", term: 0, fund: "Arts. 940-949 LFT" },
  { n: "Convenio ante el tribunal", autorFijo: null, term: 0, fund: "Art. 33 LFT" },
];
const catByName = (n) => CAT.find((c) => c.n === n);

// Clases de audiencia con respaldo en backend (etapas 10-14 → tribunal_conciliatoria + id_etapa)
const CLASE = {
  preliminar: { n: "Audiencia preliminar", etapa: 11, art: "Arts. 873-E a 873-G LFT", esq: 9 },
  juicio: { n: "Audiencia de juicio", etapa: 12, art: "Arts. 873-H y ss. LFT", esq: 10 },
  conciliatoria: { n: "Audiencia conciliatoria", etapa: 10, art: "Arts. 684-A y ss. LFT", esq: 8 },
  incidental: { n: "Audiencia incidental", etapa: 13, art: "Arts. 762-765 LFT", esq: null },
  sentencia: { n: "Audiencia de lectura de sentencia", etapa: 14, art: "Art. 885 LFT", esq: 11 },
};
// Orden de avance procesal: si hay varias audiencias pendientes, la banner de agenda
// muestra la de la etapa más adelantada (juicio sobre preliminar, etc.), no la más antigua.
const RANGO_CLASE = { incidental: 0, conciliatoria: 1, preliminar: 2, juicio: 3, sentencia: 4 };
const RES = {
  conciliatoria: ["Sin conciliación", "Convenio", "Diferida"],
  preliminar: ["Celebrada", "Diferida", "Convenio"],
  juicio: ["Celebrada", "Diferida", "Continuación señalada", "Convenio"],
  incidental: ["Celebrada", "Diferida"],
  sentencia: ["Celebrada", "Diferida"],
};

const WHO = { nos: "Despacho", act: "Contraparte", trib: "Tribunal" };
const WHO3 = [
  ["nos", "Despacho", "Nosotros — el patrón"],
  ["act", "Contraparte", "El actor"],
  ["trib", "Tribunal", "La autoridad"],
];
const ROL = { esc: "Escrito", acu: "Acuse", aut: "Auto", ced: "Cédula", act: "Acta", anx: "Anexo", cum: "Cumplimiento" };

// ── Pruebas de la demandada (catálogo de tipos + campos dinámicos por tipo) ──
const TIPO_PRUEBA_DEMANDADA_OPTS = [
  { value: "confesional", label: "Confesional" },
  { value: "testimonial", label: "Testimonial" },
  { value: "documental_publica", label: "Documental Pública" },
  { value: "documental_privada", label: "Documental Privada" },
  { value: "informe_autoridad", label: "Informe de Autoridad" },
  { value: "pericial", label: "Pericial" },
  { value: "inspeccion_ocular", label: "Inspección Ocular" },
  { value: "medios_electronicos", label: "Medios Electrónicos / Ciencia" },
  { value: "presuncional_legal_humana", label: "Presuncional legal y humana" },
  { value: "ratificacion_contenido_firma", label: "Ratificación de contenido y firma" },
];
const PERICIAL_TIPO_OPTS = [
  { value: "caligrafica_grafoscopica", label: "Caligráfica-Grafoscópica" },
  { value: "fonografica", label: "Fonográfica" },
  { value: "contable", label: "Contable" },
  { value: "medios_electronicos_informatica", label: "Medios Electrónicos - Informática" },
  { value: "psicologica", label: "Psicológica" },
  { value: "medica", label: "Médica" },
  { value: "seguridad_e_higiene", label: "Seguridad e Higiene" },
  { value: "valuacion", label: "Valuación" },
  { value: "otros", label: "Otros" },
];
const INSPECCION_OCULAR_TIPO_OPTS = [
  { value: "recibos_pago", label: "Recibos de pago" },
  { value: "nominas", label: "Nóminas" },
  { value: "control_asistencia", label: "Control de asistencia" },
  { value: "tarjetas", label: "Tarjetas" },
  { value: "altas_bajas_seguro_social", label: "Altas y bajas al seguro social" },
];
const MEDIOS_ELECTRONICOS_OPTS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "correo", label: "Correo" },
  { value: "sms", label: "SMS" },
  { value: "redes_sociales", label: "Redes sociales" },
  { value: "audios", label: "Audios" },
  { value: "videos", label: "Videos" },
  { value: "fotos", label: "Fotografías" },
  { value: "capturas", label: "Capturas" },
  { value: "plataformas", label: "Plataformas" },
  { value: "archivos", label: "Archivos digitales" },
];
// 1 campo (Confesional) hasta 3 campos (Testimonial, Pericial) según el tipo elegido.
const DEMANDADA_CAMPOS_POR_TIPO = {
  confesional: [
    { key: "absolvente", label: "Nombre de quien debe absolver posiciones", placeholder: "Ej. Nombre del actor" },
  ],
  testimonial: [
    { key: "nombre", label: "Nombre del testigo", placeholder: "Ej. Pedro Ramírez López" },
    { key: "relacion", label: "Relación con el caso", placeholder: "Ej. Compañero de trabajo" },
    { key: "contacto", label: "Contacto", placeholder: "Teléfono o email" },
  ],
  documental_publica: [
    { key: "nombre", label: "Nombre del documento", placeholder: "Ej. Acta de nacimiento" },
    { key: "descripcion", label: "Descripción", type: "textarea", span: "full", placeholder: "Descripción del contenido" },
    { key: "fecha", label: "Fecha de emisión", type: "date" },
  ],
  documental_privada: [
    { key: "nombre", label: "Nombre del documento", placeholder: "Ej. Contrato laboral" },
    { key: "descripcion", label: "Descripción", type: "textarea", span: "full", placeholder: "Descripción del contenido" },
    { key: "fecha", label: "Fecha del documento", type: "date" },
  ],
  informe_autoridad: [
    { key: "nombre", label: "Nombre del documento", placeholder: "Ej. Oficio de autoridad" },
    { key: "descripcion", label: "Descripción", type: "textarea", span: "full", placeholder: "Descripción del contenido" },
    { key: "fecha", label: "Fecha del documento", type: "date" },
  ],
  pericial: [
    { key: "tipo_pericial", label: "Tipo de pericial", type: "chips", options: PERICIAL_TIPO_OPTS, span: "full" },
    { key: "nombre_perito", label: "Perito propuesto", placeholder: "Nombre del perito" },
    { key: "objeto_prueba", label: "Objeto de la prueba", type: "textarea", span: "full" },
  ],
  inspeccion_ocular: [
    { key: "tipo_inspeccion_ocular", label: "Tipo de inspección", type: "chips", options: INSPECCION_OCULAR_TIPO_OPTS, span: "full" },
    { key: "objeto_prueba", label: "Objeto de la prueba", type: "textarea", span: "full" },
  ],
  medios_electronicos: [
    { key: "tipos", label: "Tipo(s) de medio", type: "chips", options: MEDIOS_ELECTRONICOS_OPTS, span: "full" },
    { key: "descripcion", label: "Descripción", type: "textarea", span: "full" },
  ],
  presuncional_legal_humana: [
    { key: "objeto_prueba", label: "Objeto de la prueba", type: "textarea", span: "full" },
  ],
  ratificacion_contenido_firma: [
    { key: "nombre", label: "Nombre del documento", placeholder: "Ej. Contrato laboral" },
    { key: "objeto_prueba", label: "Objeto de la prueba", type: "textarea", span: "full" },
  ],
};
const TERM_OPCIONES = [
  { value: 0, label: "Sin término" },
  { value: 3, label: "3 días" },
  { value: 5, label: "5 días" },
  { value: 8, label: "8 días" },
  { value: 10, label: "10 días" },
  { value: 15, label: "15 días" },
];

const ESQ_INDEX = {
  "Presentación de demanda": 0, "Auto de admisión de demanda": 1, "Emplazamiento": 2,
  "Contestación de demanda": 3, "Reconvención": 4, "Réplica": 5, "Contrarréplica": 6,
  "Auto de integración de la litis": 7, "Audiencia conciliatoria": 8,
  "Audiencia preliminar": 9, "Audiencia de juicio": 10,
  "Sentencia / Laudo": 11, "Audiencia de lectura de sentencia": 11,
  "Demanda de amparo directo": 12, "Demanda de amparo indirecto": 12, "Ejecutoria de amparo": 12,
  "Escrito de cumplimiento": 13, "Auto de cumplimiento y archivo": 13,
};

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fmt = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? `${d.format("DD")} ${MESES[d.month()]} ${d.format("YYYY")}` : "—";
};
const fmtHora = (v) => (v && dayjs(v).isValid() ? dayjs(v).format("HH:mm") : "");
const hoyStr = () => dayjs().format("YYYY-MM-DD");
const difDias = (a, b) => {
  const da = dayjs(a).startOf("day"), db = dayjs(b).startOf("day");
  if (!da.isValid() || !db.isValid()) return null;
  return db.diff(da, "day");
};
const isWeekend = (d) => d.day() === 0 || d.day() === 6;
const addBusinessDays = (start, n) => {
  let d = dayjs(start).startOf("day");
  let remaining = Number(n) || 0;
  while (remaining > 0) {
    d = d.add(1, "day");
    if (!isWeekend(d)) remaining -= 1;
  }
  return d;
};
// 'partes' = selector Despacho/Contraparte editable (solo cuando el autor es ambiguo) · 'fijo' = autor ya determinado por el catálogo
const modoAutor = (item) => (item?.autorFijo ? "fijo" : "partes");

const mny = (v) =>
  "$" + Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numOf = (s) => parseFloat(String(s == null ? "" : s).replace(/[^0-9.-]/g, "")) || 0;
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

function rolPorAutor(a, i, nombre) {
  const n = norm(nombre);
  if (/acuse|sello|constancia de envio|recibo/.test(n)) return "acu";
  if (/acta|minuta|videograb/.test(n)) return "act";
  if (/cedula|notificac|emplazam/.test(n)) return "ced";
  if (/auto|acuerdo|resoluc|sentencia|laudo|ejecutoria/.test(n)) return "aut";
  if (/anexo|prueba|pliego|copia|traslado/.test(n)) return "anx";
  if (a === "trib") return i === 0 ? "aut" : "anx";
  if (a === "nos") return i === 0 ? "esc" : i === 1 ? "acu" : "anx";
  return i === 0 ? "esc" : "anx";
}

const ESTADO_LBL = { acordado: "Acordado", presentado: "Presentado", revision: "En revisión", borrador: "Borrador" };

// Modal propio (portal a document.body) — evita depender del chrome de antd.
function Overlay({ open, onClose, maxWidth = 680, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="lxp-ea-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lxp-ea-modal2" style={{ maxWidth }} onMouseDown={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── Visor de documento (imagen/PDF) con zoom, rotación, imprimir y descargar ──
const MIME_POR_EXT = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", gif: "image/gif", webp: "image/webp", bmp: "image/bmp",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};
const mimeDeArchivo = (name) => {
  const ext = String(name || "").split(".").pop()?.toLowerCase();
  return MIME_POR_EXT[ext] || "application/octet-stream";
};

function DocPreviewModal({ doc, onClose }) {
  const [zoom, setZoom] = useState(100);
  const [rot, setRot] = useState(0);

  useEffect(() => {
    if (!doc) return undefined;
    setZoom(100); setRot(0);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [doc, onClose]);

  if (!doc) return null;
  const filename = doc.filename || "Documento";
  const mime = mimeDeArchivo(filename);
  const esImg = mime.startsWith("image/");
  const esPdf = mime === "application/pdf";
  const url = doc.url || null;

  const descargar = async () => {
    if (!url) return;
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      notify.error({ message: "No se pudo descargar el documento" });
    }
  };
  const imprimir = () => {
    if (!url) return;
    const w = window.open(url, "_blank", "noopener");
    if (!w) return;
    const doPrint = () => { try { w.focus(); w.print(); } catch { /* noop */ } };
    w.addEventListener("load", doPrint);
    setTimeout(doPrint, 700);
  };

  return createPortal(
    <div className="lxp-ea-pv-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lxp-ea-pv" onMouseDown={(e) => e.stopPropagation()}>
        <div className="lxp-ea-pv-h">
          <div className="lxp-ea-pv-info">
            <div className="nm">{filename}</div>
            <div className="mt">{mime}</div>
          </div>
          <button className="lxp-ea-x" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {esImg && (
          <div className="lxp-ea-pv-tools">
            <button className="lxp-ea-pv-tb" onClick={() => setZoom((z) => Math.max(25, z - 25))}>−</button>
            <span className="lxp-ea-pv-pct">{zoom}%</span>
            <button className="lxp-ea-pv-tb" onClick={() => setZoom((z) => Math.min(300, z + 25))}>+</button>
            <span className="lxp-ea-pv-sep" />
            <button className="lxp-ea-pv-tb wide" onClick={() => setRot((r) => (r - 90 + 360) % 360)}>↺ Rotar ←</button>
            <button className="lxp-ea-pv-tb wide" onClick={() => setRot((r) => (r + 90) % 360)}>↻ Rotar →</button>
            <span className="lxp-ea-spacer" />
            <button className="lxp-ea-pv-rst" onClick={() => { setZoom(100); setRot(0); }}>Restablecer</button>
          </div>
        )}

        <div className="lxp-ea-pv-body">
          {esImg ? (
            <img src={url} alt={filename} style={{ transform: `scale(${zoom / 100}) rotate(${rot}deg)` }} />
          ) : esPdf ? (
            <iframe src={url} title={filename} />
          ) : (
            <div className="lxp-ea-pv-nopreview">
              <div className="ic">📄</div>
              <p>Vista previa no disponible para este tipo de archivo.</p>
            </div>
          )}
        </div>

        <div className="lxp-ea-pv-f">
          <span className="lxp-ea-spacer" />
          <button className="lxp-ea-btn lxp-ea-ghost" onClick={imprimir}>🖨 Imprimir</button>
          <button className="lxp-ea-btn pri" onClick={descargar}>⬇ Descargar</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function TabExpedienteActuaciones({ expedienteId, onIrATerminacion, caseInfo, antecedentes, pruebasActora, onDatosActualizados }) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [actuaciones, setActuaciones] = useState([]);
  const [audEtapas, setAudEtapas] = useState({ 10: [], 11: [], 12: [], 13: [], 14: [] });
  const [terminacion, setTerminacion] = useState(null);
  const [formas, setFormas] = useState([]);
  const [filtro, setFiltro] = useState("all");
  const [docsAbiertos, setDocsAbiertos] = useState({}); // { [nodeKey]: bool } — acordeón de archivos por tarjeta
  const toggleDocsAbiertos = (key) => setDocsAbiertos((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Click en el esquema del procedimiento: lleva a la actuación correspondiente ──
  const [highlightEsq, setHighlightEsq] = useState(null);
  const [pendingScrollEsq, setPendingScrollEsq] = useState(null);
  const highlightTimeoutRef = useRef(null);

  // ── Modal actuación ──
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(null);
  const [kind, setKind] = useState(0);
  const [editId, setEditId] = useState(null);
  const [autor, setAutor] = useState("trib");
  const [fecha, setFecha] = useState(hoyStr());
  const [term, setTerm] = useState(0);
  const [notas, setNotas] = useState("");
  const [estado, setEstado] = useState("presentado");
  const [pendingFiles, setPendingFiles] = useState([]); // { file, name, rol, manual }
  const [saving, setSaving] = useState(false);
  const [pruebasDemandada, setPruebasDemandada] = useState([]); // [{ id, tipo, campos: {...} }]
  const [pdemAbiertoId, setPdemAbiertoId] = useState(null); // id de la prueba expandida (acordeón: una a la vez)
  const [defensaDemandada, setDefensaDemandada] = useState("");
  // Solo para "Requerimiento o prevención": fecha en que se cumplió y su documento de soporte.
  const [fechaCumplimiento, setFechaCumplimiento] = useState("");
  const [cumplFiles, setCumplFiles] = useState([]); // { file, name }

  // kind 1 (agendar)
  const [mClase, setMClase] = useState("preliminar");
  const [mAudF, setMAudF] = useState("");
  const [mAudH, setMAudH] = useState("10:00");

  // kind 2 (lo sucedido)
  const [resolviendo, setResolviendo] = useState(null); // { claseKey, etapa, idx, fechaSenalada }
  const [mRes, setMRes] = useState("");
  const [mFechaAud, setMFechaAud] = useState(hoyStr());
  const [mDifF, setMDifF] = useState("");
  const [mDifH, setMDifH] = useState("10:00");
  const [pruebasVeredicto, setPruebasVeredicto] = useState({}); // { [key]: "admitida" | "desechada" }

  // convenio inline
  const [cvMonto, setCvMonto] = useState("");
  const [cvForma, setCvForma] = useState("una");
  const [cvMedio, setCvMedio] = useState("Transferencia");
  const [cvFechaPago, setCvFechaPago] = useState("");
  const [cvHoraPago, setCvHoraPago] = useState("10:00");
  const [cvNotas, setCvNotas] = useState("");
  const [mparc, setMparc] = useState([]); // { fecha, hora, monto }

  // ── Modal constancia ──
  const [constOpen, setConstOpen] = useState(false);
  const [ccFecha, setCcFecha] = useState(hoyStr());
  const [ccNotas, setCcNotas] = useState("");
  const [ccFile, setCcFile] = useState(null);
  const [savingConst, setSavingConst] = useState(false);

  const fileInputRef = useRef(null);
  const ccInputRef = useRef(null);
  const cumplInputRef = useRef(null);

  // ── Visor de documento ──
  const [previewDoc, setPreviewDoc] = useState(null);

  // ── Vista ampliada de una actuación/audiencia de la línea del tiempo ──
  const [verNode, setVerNode] = useState(null);

  const cargar = useCallback(async () => {
    if (!expedienteId) return;
    setLoading(true);
    try {
      const [actRes, a10, a11, a12, a13, a14, termRes, formasRes] = await Promise.all([
        dispatch(actionTribunalActuacionesGet(expedienteId)),
        dispatch(actionTribunalAudienciaGet(expedienteId, 10)),
        dispatch(actionTribunalAudienciaGet(expedienteId, 11)),
        dispatch(actionTribunalAudienciaGet(expedienteId, 12)),
        dispatch(actionTribunalAudienciaGet(expedienteId, 13)),
        dispatch(actionTribunalAudienciaGet(expedienteId, 14)),
        dispatch(actionGetTerminacion(expedienteId)),
        dispatch(actionGetFormasTerminacion()),
      ]);
      setActuaciones(actRes?.items || []);
      setAudEtapas({
        10: a10?.items || [], 11: a11?.items || [], 12: a12?.items || [],
        13: a13?.items || [], 14: a14?.items || [],
      });
      setTerminacion(termRes?.terminacion?.item || null);
      setFormas(formasRes?.items || formasRes || []);
    } finally {
      setLoading(false);
    }
    // Cada actuación/audiencia/terminación capturada aquí puede mover la etapa
    // real del expediente — se avisa al padre para que refresque el badge de
    // estatus de arriba (computeTribunalEstatus) sin esperar un reload manual.
    onDatosActualizados?.();
  }, [dispatch, expedienteId, onDatosActualizados]);

  useEffect(() => { cargar(); }, [cargar]);

  const nombreForma = useCallback((id) => {
    const f = (formas || []).find((x) => String(x.id_forma_terminacion) === String(id));
    return f?.nombre || null;
  }, [formas]);

  // Actuaciones "reales" a listar en la línea del tiempo — excluye las que solo cargan
  // documentos de una audiencia celebrada (ver AUDIENCIA_DOC_PREFIX).
  const actuacionesVisibles = useMemo(
    () => actuaciones.filter((a) => !String(a.tipo || "").startsWith(AUDIENCIA_DOC_PREFIX)),
    [actuaciones]
  );

  // ── Slots de audiencia por clase ──
  const slotsPorClase = useMemo(() => {
    const out = {};
    Object.entries(CLASE).forEach(([claseKey, meta]) => {
      const items = audEtapas[meta.etapa] || [];
      const bySlot = {};
      items.forEach((it) => { bySlot[Number(it.audiencia_diferida)] = it; });
      out[claseKey] = [bySlot[0] || null, bySlot[1] || null, bySlot[2] || null];
    });
    return out;
  }, [audEtapas]);

  const nextOpenSlot = (claseKey) => {
    const slots = slotsPorClase[claseKey] || [];
    for (let i = 0; i < 3; i++) if (!slots[i]?.fecha_audiencia) return i;
    return null;
  };

  const agendaNodes = useMemo(() => {
    const nodes = [];
    Object.entries(CLASE).forEach(([claseKey, meta]) => {
      (slotsPorClase[claseKey] || []).forEach((slot, idx) => {
        if (slot?.fecha_audiencia && !slot.resultado) nodes.push({ claseKey, meta, slot, idx });
      });
    });
    nodes.sort((a, b) => new Date(a.slot.fecha_audiencia) - new Date(b.slot.fecha_audiencia));
    return nodes;
  }, [slotsPorClase]);

  const proxima = useMemo(() => {
    let best = null;
    agendaNodes.forEach((n) => {
      if (!best || (RANGO_CLASE[n.claseKey] ?? 0) > (RANGO_CLASE[best.claseKey] ?? 0)) best = n;
    });
    return best;
  }, [agendaNodes]);

  const audienciaEventos = useMemo(() => {
    const evs = [];
    Object.entries(CLASE).forEach(([claseKey, meta]) => {
      (slotsPorClase[claseKey] || []).forEach((slot, idx) => {
        if (slot?.resultado) {
          const tipoStr = `${AUDIENCIA_DOC_PREFIX}${meta.n}`;
          const diaSlot = slot.fecha_audiencia ? dayjs(slot.fecha_audiencia).format("YYYY-MM-DD") : null;
          const docActuacion = actuaciones.find(
            (a) => a.tipo === tipoStr && a.fecha && dayjs(a.fecha).format("YYYY-MM-DD") === diaSlot
          );
          evs.push({
            id: `aud-${meta.etapa}-${idx}`,
            fecha: slot.fecha_audiencia,
            tipo: tipoStr,
            autor: "trib",
            resultado: slot.resultado,
            notas: slot.notas,
            pruebasResultado: Array.isArray(slot.pruebas_resultado) ? slot.pruebas_resultado : [],
            fundamento: meta.art,
            isAudiencia: true,
            documentos: docActuacion?.documentos || [],
            claseKey, etapa: meta.etapa, slotIdx: idx,
          });
        }
      });
    });
    return evs;
  }, [slotsPorClase, actuaciones]);

  // ── Timeline unificado ──
  const nodosTimeline = useMemo(() => {
    const normales = actuacionesVisibles.map((a) => ({ ...a, isAudiencia: false, fechaOrden: a.fecha }));
    // La demanda capturada al crear el expediente aparece sola como primera actuación
    // (con su documento) mientras no se haya registrado una "Presentación de demanda" manual.
    const dem = antecedentes?.demanda;
    if ((dem || antecedentes?.presentacionDemanda) && !actuacionesVisibles.some((a) => a.tipo === "Presentación de demanda")) {
      normales.push({
        id: "demanda-auto", isDemandaAuto: true, isAudiencia: false,
        tipo: "Presentación de demanda", autor: "act",
        fecha: antecedentes?.presentacionDemanda || null, fechaOrden: antecedentes?.presentacionDemanda || 0,
        fundamento: catByName("Presentación de demanda")?.fund,
        documentos: dem ? [{ id: "demanda-auto-doc", filename: dem.filename, url: dem.url, rol: "esc" }] : [],
      });
    }
    const audRes = audienciaEventos.map((a) => ({ ...a, fechaOrden: a.fecha }));
    const virtuales = agendaNodes.map((n) => ({
      isVirtual: true, claseKey: n.claseKey, meta: n.meta, slot: n.slot, idx: n.idx,
      fechaOrden: n.slot.fecha_audiencia,
    }));
    // La terminación del expediente también aparece como un evento más en la línea
    // del tiempo (se captura en el tab "Terminación", pero debe verse aquí también).
    const terminacionNodes = terminacion ? [{
      id: "terminacion", isTerminacion: true, isAudiencia: false, autor: "trib",
      tipo: `Terminación del expediente — ${nombreForma(terminacion.id_forma_terminacion) || "Concluido"}`,
      fecha: terminacion.fecha_terminacion, fechaOrden: terminacion.fecha_terminacion,
      notas: terminacion.observaciones || null,
    }] : [];
    const todos = [...normales, ...audRes, ...virtuales, ...terminacionNodes];
    todos.sort((a, b) => {
      const da = new Date(a.fechaOrden), db = new Date(b.fechaOrden);
      if (da - db !== 0) return da - db;
      return (a.isVirtual ? 1 : 0) - (b.isVirtual ? 1 : 0);
    });
    return todos;
  }, [actuacionesVisibles, audienciaEventos, agendaNodes, antecedentes, terminacion, nombreForma]);

  // Mapa etapa del esquema -> nodo del timeline, para abrir la vista ampliada
  // directamente al dar click en un paso del Esquema del Procedimiento.
  const nodoPorEsq = useMemo(() => {
    const map = {};
    nodosTimeline.forEach((n) => {
      const esqIdx = n.isVirtual
        ? n.meta.esq
        : ESQ_INDEX[n.isAudiencia ? n.tipo.replace(AUDIENCIA_DOC_PREFIX, "") : n.tipo];
      if (esqIdx != null) map[esqIdx] = n;
    });
    return map;
  }, [nodosTimeline]);

  const handleEtapaClick = useCallback((idx) => {
    const n = nodoPorEsq[idx];
    if (n) {
      setVerNode(n);
      return;
    }
    setFiltro("all");
    setPendingScrollEsq(idx);
  }, [nodoPorEsq]);

  const visibles = useMemo(() => nodosTimeline.filter((n) => {
    if (filtro === "all") return true;
    if (filtro === "aud") return n.isVirtual || n.isAudiencia;
    return !n.isVirtual && n.autor === filtro;
  }), [nodosTimeline, filtro]);

  // Mismo contenido de `visibles`, pero con el número de secuencia cronológico
  // fijo (1..N) y mostrado de la más reciente a la más antigua.
  const visiblesDesc = useMemo(
    () => visibles.map((n, i) => ({ ...n, _seq: i + 1 })).reverse(),
    [visibles]
  );

  // Espera a que `visibles` refleje el filtro "all" (tras un click en el esquema)
  // para poder encontrar el nodo en el DOM, hacer scroll y resaltarlo un momento.
  useEffect(() => {
    if (pendingScrollEsq == null) return;
    const el = document.getElementById(`lxp-ea-node-${pendingScrollEsq}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightEsq(pendingScrollEsq);
    setPendingScrollEsq(null);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => setHighlightEsq(null), 2200);
  }, [pendingScrollEsq, visiblesDesc]);

  useEffect(() => () => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
  }, []);

  // ── Avance del procedimiento ──
  const hechas = useMemo(() => {
    const set = new Set();
    actuacionesVisibles.forEach((a) => { if (ESQ_INDEX[a.tipo] !== undefined) set.add(ESQ_INDEX[a.tipo]); });
    audienciaEventos.forEach((a) => {
      const clean = a.tipo.replace(AUDIENCIA_DOC_PREFIX, "");
      if (ESQ_INDEX[clean] !== undefined) set.add(ESQ_INDEX[clean]);
    });
    // Datos capturados al crear el expediente
    if (antecedentes?.presentacionDemanda || antecedentes?.demanda) set.add(0);
    if (antecedentes?.notificacionDemanda) set.add(2);
    // Ojo: aquí NO se rellenan como "cumplidas" las etapas anteriores a la más
    // lejana alcanzada. Si el expediente ya tiene una audiencia agendada más
    // adelante pero se saltó etapas intermedias sin registrarlas, esas etapas
    // deben seguir como no cumplidas — es justo lo que EsquemaProcedimiento usa
    // (junto con agendadasSet) para marcarlas con el aviso "Falta de subir" en
    // vez de darlas por hechas en silencio.
    if (terminacion) set.add(14);
    return set;
  }, [actuacionesVisibles, audienciaEventos, terminacion, antecedentes]);

  // Fecha en la que se cumplió cada etapa (misma lógica/fuentes que `hechas`),
  // para mostrarla junto al icono en EsquemaProcedimiento.
  const hechasFechas = useMemo(() => {
    const map = {};
    actuacionesVisibles.forEach((a) => {
      const idx = ESQ_INDEX[a.tipo];
      if (idx !== undefined && a.fecha) map[idx] = a.fecha;
    });
    audienciaEventos.forEach((a) => {
      const clean = a.tipo.replace(AUDIENCIA_DOC_PREFIX, "");
      const idx = ESQ_INDEX[clean];
      if (idx !== undefined && a.fecha) map[idx] = a.fecha;
    });
    if (antecedentes?.presentacionDemanda && map[0] === undefined) map[0] = antecedentes.presentacionDemanda;
    if (antecedentes?.notificacionDemanda && map[2] === undefined) map[2] = antecedentes.notificacionDemanda;
    if (terminacion?.fecha_terminacion) map[14] = terminacion.fecha_terminacion;
    return map;
  }, [actuacionesVisibles, audienciaEventos, terminacion, antecedentes]);

  const agendadasSet = useMemo(() => {
    const set = new Set();
    agendaNodes.forEach((n) => { if (n.meta.esq != null) set.add(n.meta.esq); });
    return set;
  }, [agendaNodes]);

  const agendadasFechas = useMemo(() => {
    const map = {};
    agendaNodes.forEach((n) => {
      if (n.meta.esq != null && n.slot.fecha_audiencia) map[n.meta.esq] = n.slot.fecha_audiencia;
    });
    return map;
  }, [agendaNodes]);

  // ── Pruebas de la demandada ofrecidas en la contestación (para calificarlas en la audiencia preliminar) ──
  const pruebasContestacionDemandada = useMemo(() => {
    const out = [];
    actuaciones
      .filter((a) => a.tipo === "Contestación de demanda")
      .forEach((a) => {
        (Array.isArray(a.pruebas_demandada) ? a.pruebas_demandada : []).forEach((p, idx) => {
          if (!p?.tipo) return;
          out.push({ key: `${a.id_tribunal_actuacion}-${idx}`, tipo: p.tipo, campos: p.campos || {} });
        });
      });
    return out;
  }, [actuaciones]);

  // ── Pruebas de la actora ofrecidas en la demanda (vienen ya aplanadas desde la
  // página padre) — para calificarlas junto con las de la demandada en la misma vista.
  const pruebasContestacionActora = useMemo(
    () => (Array.isArray(pruebasActora) ? pruebasActora : [])
      .filter((p) => p?.tipo)
      .map((p, idx) => ({ key: `actora-${idx}`, tipo: p.tipo, resumen: p.resumen || "" })),
    [pruebasActora]
  );

  // ── Resumen ──
  const countBy = (k) => actuacionesVisibles.filter((a) => a.autor === k).length;
  const totalDocs = actuaciones.reduce((acc, a) => acc + (a.documentos?.length || 0), 0);
  const escritosDespacho = actuaciones.filter((a) => a.autor === "nos");
  const terminacionLbl = terminacion ? (nombreForma(terminacion.id_forma_terminacion) || "Concluido") : "En trámite";

  // ── Banner "hint": preliminar celebrada sin juicio agendado ──
  const hintJuicio = useMemo(() => {
    if (proxima) return false;
    const prelimResuelta = audienciaEventos.some(
      (a) => a.claseKey === "preliminar" && a.resultado && !/diferida/i.test(a.resultado)
    );
    const juicioActivo =
      audienciaEventos.some((a) => a.claseKey === "juicio") ||
      agendaNodes.some((n) => n.claseKey === "juicio");
    return prelimResuelta && !juicioActivo;
  }, [proxima, audienciaEventos, agendaNodes]);

  // ── Antecedentes ──
  const ant = antecedentes || {};
  const antDias = useMemo(() => {
    const d1 = difDias(ant.ultimoDiaLaborado, ant.citatorioCCL);
    const d3 = difDias(ant.constanciaNoConciliacion, ant.presentacionDemanda);
    if (d1 == null && d3 == null) return "—";
    return `${(d1 || 0) + (d3 || 0)} días`;
  }, [ant.ultimoDiaLaborado, ant.citatorioCCL, ant.constanciaNoConciliacion, ant.presentacionDemanda]);

  // ── Modal: reset / abrir / cerrar ──
  const resetModal = () => {
    setStep(1); setQuery(""); setSel(null); setKind(0); setEditId(null);
    setAutor("trib"); setFecha(hoyStr()); setTerm(0); setNotas("");
    setEstado("presentado"); setPendingFiles([]); setPruebasDemandada([]); setPdemAbiertoId(null);
    setDefensaDemandada("");
    setFechaCumplimiento(""); setCumplFiles([]);
    setMClase("preliminar"); setMAudF(""); setMAudH("10:00");
    setResolviendo(null); setMRes(""); setMFechaAud(hoyStr()); setMDifF(""); setMDifH("10:00");
    setPruebasVeredicto({});
    setCvMonto(""); setCvForma("una"); setCvMedio("Transferencia");
    setCvFechaPago(""); setCvHoraPago("10:00"); setCvNotas(""); setMparc([]);
  };
  const abrirModal = () => { resetModal(); setModalOpen(true); };
  const cerrarModal = () => { setModalOpen(false); resetModal(); };

  const pick = (item) => {
    setSel(item.n); setKind(item.kind || 0);
    if (item.kind === 1) setAutor("trib");
    else if (item.autorFijo) setAutor(item.autorFijo);
    else setAutor("nos");
    setTerm(item.term || 0);
    setStep(2);
  };

  const nuevoSenalamiento = (claseKey) => {
    resetModal();
    setSel("Audiencia"); setKind(1); setTerm(catByName("Audiencia")?.term || 0);
    if (claseKey) setMClase(claseKey);
    setStep(2);
    setModalOpen(true);
  };

  const abrirRegistrarSucedido = (claseKey, idx) => {
    resetModal();
    const meta = CLASE[claseKey];
    const slot = slotsPorClase[claseKey]?.[idx];
    setKind(2); setSel("Audiencia celebrada");
    setResolviendo({ claseKey, etapa: meta.etapa, idx, fechaSenalada: slot?.fecha_audiencia });
    setMFechaAud(slot?.fecha_audiencia ? dayjs(slot.fecha_audiencia).format("YYYY-MM-DD") : hoyStr());
    setStep(2); setModalOpen(true);
  };

  // Reabre "lo sucedido" de una audiencia YA celebrada para corregir resultado/fecha/notas/pruebas.
  // El guardado reutiliza guardarSucedido: como audiencia_diferida es la llave del upsert, vuelve
  // a escribir la misma sesión en vez de crear una nueva.
  const editarAudienciaSucedido = (n) => {
    resetModal();
    setKind(2); setSel("Audiencia celebrada");
    setResolviendo({ claseKey: n.claseKey, etapa: n.etapa, idx: n.slotIdx, fechaSenalada: n.fecha });
    setMFechaAud(n.fecha ? dayjs(n.fecha).format("YYYY-MM-DD") : hoyStr());
    setMRes(n.resultado || "");
    setNotas(n.notas || "");
    if (Array.isArray(n.pruebasResultado) && n.pruebasResultado.length) {
      const ver = {};
      const guardadasDemandada = n.pruebasResultado.filter((r) => (r.parte || "demandada") === "demandada");
      pruebasContestacionDemandada.forEach((p, idx) => {
        const guardada =
          guardadasDemandada[idx]?.tipo === p.tipo
            ? guardadasDemandada[idx]
            : guardadasDemandada.find(
                (r) => r.tipo === p.tipo && JSON.stringify(r.campos || {}) === JSON.stringify(p.campos || {})
              );
        if (guardada?.resultado) ver[p.key] = guardada.resultado;
      });
      const guardadasActora = n.pruebasResultado.filter((r) => r.parte === "actora");
      pruebasContestacionActora.forEach((p, idx) => {
        const guardada =
          guardadasActora[idx]?.tipo === p.tipo
            ? guardadasActora[idx]
            : guardadasActora.find((r) => r.tipo === p.tipo && (r.resumen || "") === (p.resumen || ""));
        if (guardada?.resultado) ver[p.key] = guardada.resultado;
      });
      setPruebasVeredicto(ver);
    }
    setStep(2); setModalOpen(true);
  };

  const editarActuacion = (a) => {
    resetModal();
    const item = catByName(a.tipo);
    setEditId(a.id_tribunal_actuacion);
    setSel(a.tipo); setKind(0);
    setAutor(a.autor);
    setFecha(a.fecha ? dayjs(a.fecha).format("YYYY-MM-DD") : hoyStr());
    setTerm(a.term_dias || item?.term || 0);
    setNotas(a.notas || ""); setEstado(a.estado || "presentado");
    setPruebasDemandada(Array.isArray(a.pruebas_demandada) ? a.pruebas_demandada : []);
    setDefensaDemandada(a.defensa_demandada || "");
    setFechaCumplimiento(a.fecha_cumplimiento_requerimiento ? dayjs(a.fecha_cumplimiento_requerimiento).format("YYYY-MM-DD") : "");
    setStep(2); setModalOpen(true);
  };

  // ── Pruebas de la demandada (tabla dinámica) ──
  const addPruebaDemandada = () => {
    const nuevoId = Date.now() + Math.random();
    setPruebasDemandada((prev) => [...prev, { id: nuevoId, tipo: "", campos: {} }]);
    setPdemAbiertoId(nuevoId);
  };
  const removePruebaDemandada = (id) => {
    setPruebasDemandada((prev) => prev.filter((item) => item.id !== id));
    setPdemAbiertoId((cur) => (cur === id ? null : cur));
  };
  const setPruebaDemandadaTipo = (id, tipo) => {
    setPruebasDemandada((prev) => prev.map((item) => (item.id === id ? { ...item, tipo, campos: {} } : item)));
  };
  // Vista previa de una línea para mostrar cuando la prueba está colapsada (sin tener que expandirla).
  const resumenPruebaDemandada = (p) => {
    const campos = DEMANDADA_CAMPOS_POR_TIPO[p.tipo] || [];
    for (const campo of campos) {
      const v = p.campos?.[campo.key];
      if (campo.type === "chips") {
        if (Array.isArray(v) && v.length) {
          return v.map((val) => campo.options.find((o) => o.value === val)?.label || val).join(", ");
        }
        continue;
      }
      if (v) return v;
    }
    return "";
  };
  const setPruebaDemandadaCampo = (id, key, value) => {
    setPruebasDemandada((prev) =>
      prev.map((item) => (item.id === id ? { ...item, campos: { ...item.campos, [key]: value } } : item))
    );
  };
  const toggleChipPruebaDemandada = (id, key, value) => {
    setPruebasDemandada((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const actuales = Array.isArray(item.campos?.[key]) ? item.campos[key] : [];
        const nuevos = actuales.includes(value) ? actuales.filter((v) => v !== value) : [...actuales, value];
        return { ...item, campos: { ...item.campos, [key]: nuevos } };
      })
    );
  };

  const eliminarActuacion = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.tipo}" del ${fmt(a.fecha)}?`)) return;
    try {
      await dispatch(actionTribunalActuacionDelete(expedienteId, a.id_tribunal_actuacion));
      notify.success({ message: "Actuación eliminada" });
      cargar();
    } catch { notify.error({ message: "No se pudo eliminar" }); }
  };
  const eliminarDesdeModal = () => {
    const a = actuaciones.find((x) => x.id_tribunal_actuacion === editId);
    if (a) { cerrarModal(); eliminarActuacion(a); }
  };

  const eliminarAudienciaSlot = async (etapa, idx, nombre, fechaAud) => {
    if (!window.confirm(`¿Eliminar "${nombre}" del ${fmt(fechaAud)}?`)) return;
    try {
      await dispatch(actionTribunalAudienciaDelete(expedienteId, etapa, idx));
      notify.success({ message: "Audiencia eliminada" });
      cargar();
    } catch { notify.error({ message: "No se pudo eliminar la audiencia" }); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const groups = [];
    let current = null;
    CAT.forEach((c) => {
      if (c.g) { current = { g: c.g, items: [] }; groups.push(current); return; }
      if (q && !c.n.toLowerCase().includes(q) && !(c.fund || "").toLowerCase().includes(q)) return;
      current?.items.push(c);
    });
    return groups.filter((g) => g.items.length > 0);
  }, [query]);

  // ── Documentos: dropzone ──
  const addFiles = (list) => {
    const arr = [...list];
    setPendingFiles((prev) => {
      const out = [...prev];
      arr.forEach((f) => out.push({ file: f, name: f.name, rol: rolPorAutor(autor, out.length, f.name), manual: false }));
      return out;
    });
  };
  const setRolFile = (i, rol) => setPendingFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, rol, manual: true } : f)));
  const rmFile = (i) => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i));
  // re-derivar roles no manuales al cambiar autor
  useEffect(() => {
    setPendingFiles((prev) => prev.map((f, i) => (f.manual ? f : { ...f, rol: rolPorAutor(autor, i, f.name) })));
  }, [autor]);

  const subirDocumentos = async (idActuacion) => {
    for (const f of pendingFiles) {
      try {
        await dispatch(actionTribunalActuacionUploadDocumento(expedienteId, idActuacion, f.file, f.rol || null));
      } catch { notify.error({ message: `No se pudo subir ${f.name}` }); }
    }
  };

  // ── Documentos del cumplimiento del requerimiento (solo "Requerimiento o prevención") ──
  const addCumplFiles = (list) => {
    const arr = [...list];
    setCumplFiles((prev) => [...prev, ...arr.map((f) => ({ file: f, name: f.name }))]);
  };
  const rmCumplFile = (i) => setCumplFiles((prev) => prev.filter((_, idx) => idx !== i));
  const subirCumplDocumentos = async (idActuacion) => {
    for (const f of cumplFiles) {
      try {
        await dispatch(actionTribunalActuacionUploadDocumento(expedienteId, idActuacion, f.file, "cum"));
      } catch { notify.error({ message: `No se pudo subir ${f.name}` }); }
    }
  };

  // ── Guardar: normal ──
  const guardarNormal = async () => {
    if (!sel) { notify.error({ message: "Selecciona la actuación" }); return; }
    if (!fecha) { notify.error({ message: "Falta la fecha" }); return; }
    let cv = null;
    if (esConvenioActuacion) { cv = validarConvenio(); if (!cv) return; }
    setSaving(true);
    try {
      const n = Number(term) || 0;
      const payload = {
        tipo: sel, autor, fecha,
        term_dias: n || null,
        estado: autor === "nos" ? estado : null,
        fundamento: catByName(sel)?.fund || null,
        notas: notas.trim() || null,
        pruebas_demandada: pruebasDemandada.filter((p) => p.tipo).length ? pruebasDemandada.filter((p) => p.tipo) : null,
        defensa_demandada: defensaDemandada.trim() || null,
        fecha_cumplimiento_requerimiento: sel === "Requerimiento o prevención" ? (fechaCumplimiento || null) : null,
      };
      let item;
      if (editId) item = (await dispatch(actionTribunalActuacionUpdate(expedienteId, editId, payload)))?.item;
      else item = (await dispatch(actionTribunalActuacionCreate(expedienteId, payload)))?.item;
      if (item && pendingFiles.length) await subirDocumentos(item.id_tribunal_actuacion);
      if (item && cumplFiles.length) await subirCumplDocumentos(item.id_tribunal_actuacion);

      if (esConvenioActuacion) {
        let formasList = formas;
        if (!formasList?.length) {
          const r = await dispatch(actionGetFormasTerminacion());
          formasList = r?.items || r || [];
        }
        const formaConv = (formasList || []).find((f) => norm(f.nombre) === "convenio");
        await dispatch(actionSaveTerminacion(expedienteId, {
          id_forma_terminacion: formaConv ? formaConv.id_forma_terminacion : 1,
          fecha_terminacion: fecha,
          etapa: "",
          observaciones: cvNotas.trim() || "",
          active: 1,
          monto_total: String(cv.monto),
          tipo_pago: cv.forma === "parcialidades" ? 2 : 1,
          fecha_pago: cv.forma === "una" ? `${cv.fechaPago} ${cv.horaPago}` : null,
          parcialidades:
            cv.forma === "parcialidades"
              ? cv.parcialidades.map((p) => ({ monto: String(p.monto), fecha_pago: p.fecha }))
              : [],
        }));
      }

      notify.success({ message: editId ? "Actuación actualizada" : "Actuación registrada" });
      cerrarModal(); cargar();
    } catch { notify.error({ message: "No se pudo guardar la actuación" }); }
    finally { setSaving(false); }
  };

  // ── Guardar: señalamiento ──
  const guardarSenalamiento = async () => {
    if (!mAudF) { notify.error({ message: "Falta la fecha de la audiencia" }); return; }
    const slotIdx = nextOpenSlot(mClase);
    if (slotIdx === null) { notify.error({ message: "Ya se agotaron las sesiones disponibles para esta clase" }); return; }
    setSaving(true);
    try {
      const fechaHora = `${mAudF} ${mAudH || "10:00"}:00`;
      await dispatch(actionTribunalAudienciaUpsert(expedienteId, CLASE[mClase].etapa, {
        fecha_audiencia: fechaHora, resultado: null, notas: notas.trim() || null, audiencia_diferida: slotIdx,
      }));

      const payload = {
        tipo: CLASE[mClase]?.n || sel, autor: "trib", fecha: fecha || hoyStr(),
        term_dias: null, estado: null,
        fundamento: CLASE[mClase]?.art || catByName(sel)?.fund || null,
        notas: notas.trim() || null,
      };
      const item = (await dispatch(actionTribunalActuacionCreate(expedienteId, payload)))?.item;
      if (item && pendingFiles.length) await subirDocumentos(item.id_tribunal_actuacion);

      notify.success({ message: `Audiencia agendada para el ${fmt(mAudF)}` });
      cerrarModal(); cargar();
    } catch { notify.error({ message: "No se pudo agendar la audiencia" }); }
    finally { setSaving(false); }
  };

  // ── Convenio ──
  const cvSuma = useMemo(() => mparc.reduce((a, p) => a + numOf(p.monto), 0), [mparc]);
  const cvDif = +(numOf(cvMonto) - cvSuma).toFixed(2);
  const addParc = () => setMparc((p) => [...p, { fecha: "", hora: "10:00", monto: "" }]);
  const rmParc = (i) => setMparc((p) => p.filter((_, idx) => idx !== i));
  const setParc = (i, k, v) => setMparc((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const validarConvenio = () => {
    const monto = numOf(cvMonto);
    if (!monto) { notify.error({ message: "Falta el monto total del convenio" }); return null; }
    if (cvForma === "una" && !cvFechaPago) { notify.error({ message: "Falta la fecha de pago" }); return null; }
    if (cvForma === "parcialidades") {
      const items = mparc.filter((p) => p.fecha || p.monto);
      if (!items.length) { notify.error({ message: "Agrega al menos una parcialidad" }); return null; }
      if (items.some((p) => !p.fecha || !numOf(p.monto))) {
        notify.error({ message: "Cada parcialidad necesita fecha y monto" }); return null;
      }
      const suma = items.reduce((a, p) => a + numOf(p.monto), 0);
      if (Math.abs(suma - monto) >= 0.01) {
        notify.warning({ message: `Aviso: las parcialidades suman ${mny(suma)} y el total es ${mny(monto)}` });
      }
      return { monto, forma: "parcialidades", parcialidades: items.map((p) => ({ fecha: p.fecha, monto: numOf(p.monto) })) };
    }
    return { monto, forma: "una", fechaPago: cvFechaPago, horaPago: cvHoraPago || "10:00" };
  };

  // ── Guardar: lo sucedido ──
  const guardarSucedido = async () => {
    if (!resolviendo) return;
    if (!mRes) { notify.error({ message: "Selecciona el resultado" }); return; }
    if (!mFechaAud) { notify.error({ message: "Falta la fecha en que se celebró" }); return; }
    const esDif = /diferida/i.test(mRes);
    const esConv = /convenio/i.test(mRes);
    if (esDif && !mDifF) { notify.error({ message: "Indica la nueva fecha señalada" }); return; }
    let cv = null;
    if (esConv) { cv = validarConvenio(); if (!cv) return; }
    setSaving(true);
    try {
      const { etapa, idx, claseKey } = resolviendo;
      const pruebasResultadoPayload =
        claseKey === "preliminar"
          ? [
              ...pruebasContestacionDemandada
                .filter((p) => pruebasVeredicto[p.key])
                .map((p) => ({ parte: "demandada", tipo: p.tipo, campos: p.campos, resultado: pruebasVeredicto[p.key] })),
              ...pruebasContestacionActora
                .filter((p) => pruebasVeredicto[p.key])
                .map((p) => ({ parte: "actora", tipo: p.tipo, resumen: p.resumen, resultado: pruebasVeredicto[p.key] })),
            ]
          : null;
      await dispatch(actionTribunalAudienciaUpsert(expedienteId, etapa, {
        fecha_audiencia: resolviendo.fechaSenalada || `${mFechaAud} 10:00:00`,
        resultado: mRes, notas: notas.trim() || null, audiencia_diferida: idx,
        pruebas_resultado: pruebasResultadoPayload?.length ? pruebasResultadoPayload : null,
      }));
      if (esDif && idx < 2) {
        await dispatch(actionTribunalAudienciaUpsert(expedienteId, etapa, {
          fecha_audiencia: `${mDifF} ${mDifH || "10:00"}:00`, resultado: null, notas: null, audiencia_diferida: idx + 1,
        }));
      }
      if (esConv) {
        let formasList = formas;
        if (!formasList?.length) {
          const r = await dispatch(actionGetFormasTerminacion());
          formasList = r?.items || r || [];
        }
        const formaConv = (formasList || []).find((f) => norm(f.nombre) === "convenio");
        await dispatch(actionSaveTerminacion(expedienteId, {
          id_forma_terminacion: formaConv ? formaConv.id_forma_terminacion : 1,
          fecha_terminacion: mFechaAud,
          etapa: "audiencia",
          observaciones: cvNotas.trim() || "",
          active: 1,
          monto_total: String(cv.monto),
          tipo_pago: cv.forma === "parcialidades" ? 2 : 1,
          fecha_pago: cv.forma === "una" ? `${cv.fechaPago} ${cv.horaPago}` : null,
          parcialidades:
            cv.forma === "parcialidades"
              ? cv.parcialidades.map((p) => ({ monto: String(p.monto), fecha_pago: p.fecha }))
              : [],
        }));
      }
      if (pendingFiles.length) {
        const meta = CLASE[resolviendo.claseKey];
        const docItem = (await dispatch(actionTribunalActuacionCreate(expedienteId, {
          tipo: `${AUDIENCIA_DOC_PREFIX}${meta.n}`,
          autor: "trib", fecha: mFechaAud,
          term_dias: null, estado: null,
          fundamento: meta.art || null, notas: null,
        })))?.item;
        if (docItem) await subirDocumentos(docItem.id_tribunal_actuacion);
      }
      notify.success({
        message: esDif
          ? `Audiencia diferida — nueva fecha: ${fmt(mDifF)}`
          : esConv
            ? "Convenio registrado"
            : "Resultado registrado",
      });
      cerrarModal(); cargar();
    } catch { notify.error({ message: "No se pudo guardar el resultado" }); }
    finally { setSaving(false); }
  };

  const guardar = () => {
    if (kind === 1) return guardarSenalamiento();
    if (kind === 2) return guardarSucedido();
    return guardarNormal();
  };

  // ── Constancia ──
  const abrirConstancia = () => {
    setCcFecha(hoyStr()); setCcNotas(""); setCcFile(null); setConstOpen(true);
  };
  const guardarConstancia = async () => {
    if (!ccFecha) { notify.error({ message: "Falta la fecha de la constancia" }); return; }
    setSavingConst(true);
    try {
      await dispatch(actionSaveConstanciaTerminacion(expedienteId, {
        fecha_cumplimiento: ccFecha, notas_cumplimiento: ccNotas.trim() || "", file: ccFile,
      }));
      notify.success({ message: "Constancia registrada — expediente concluido" });
      setConstOpen(false); cargar();
    } catch { notify.error({ message: "No se pudo guardar la constancia" }); }
    finally { setSavingConst(false); }
  };

  // ── Documentos: abrir en el visor ──
  const abrirDoc = (doc) => {
    const rawUrl = doc?.url || doc?.constancia_url || null;
    if (!rawUrl) { notify.warning({ message: "El documento no está disponible" }); return; }
    const url = `${PATH}${rawUrl}`;
    const downloadUrl = `${PATH}${rawUrl.replace(/^\/uploads\//, "/uploads-download/")}?name=${encodeURIComponent(doc.filename || "documento")}`;
    setPreviewDoc({ filename: doc.filename, url, downloadUrl });
  };

  const eliminarDoc = async (e, doc) => {
    e.stopPropagation();
    if (!doc?.id) return;
    if (!window.confirm(`¿Eliminar el documento "${doc.filename}"?`)) return;
    try {
      await dispatch(actionTribunalActuacionDocumentoDelete(doc.id));
      notify.success({ message: "Documento eliminado" });
      cargar();
    } catch { notify.error({ message: "No se pudo eliminar el documento" }); }
  };

  // ── Derivados de modal ──
  const selCat = sel ? catByName(sel) : null;
  const modo = selCat ? modoAutor(selCat) : "fijo";
  const esConvenioModal = kind === 2 && /convenio/i.test(mRes || "");
  const esConvenioActuacion = kind === 0 && sel === "Convenio ante el tribunal";
  const nTerm = Number(term) || 0;
  const lbFecha =
    kind === 1 ? "Fecha de notificación del señalamiento"
      : kind === 2 ? "Fecha de registro"
        : nTerm ? "Fecha de notificación" : "Fecha de la actuación";
  const showWho = kind === 0 && modo === "partes" && !esConvenioActuacion;
  const showEstado = kind === 0 && autor === "nos" && !esConvenioActuacion;
  const convenioCamposBox = (
    <div className="lxp-ea-cvbox">
      <div className="l">Datos del convenio</div>
      <div className="lxp-ea-grid3">
        <div className="lxp-ea-fld"><label>Monto total del convenio</label>
          <input type="text" inputMode="decimal" placeholder="$0.00" value={cvMonto}
            onChange={(e) => setCvMonto(e.target.value)}
            onBlur={(e) => setCvMonto(e.target.value.trim() ? mny(numOf(e.target.value)) : "")} /></div>
        <div className="lxp-ea-fld"><label>Forma de pago</label>
          <select value={cvForma} onChange={(e) => { setCvForma(e.target.value); if (e.target.value === "parcialidades" && !mparc.length) setMparc([{ fecha: "", hora: "10:00", monto: "" }]); }}>
            <option value="una">Una exhibición</option>
            <option value="parcialidades">Parcialidades</option>
          </select></div>
        <div className="lxp-ea-fld"><label>Medio de pago</label>
          <select value={cvMedio} onChange={(e) => setCvMedio(e.target.value)}>
            <option>Transferencia</option><option>Cheque</option><option>Efectivo</option>
          </select></div>
      </div>

      {cvForma === "una" ? (
        <div className="lxp-ea-grid3" style={{ marginTop: 12 }}>
          <div className="lxp-ea-fld"><label>Fecha de pago</label>
            <ScrollDateInput value={cvFechaPago} onChange={setCvFechaPago} style={{ width: "100%" }} /></div>
          <div className="lxp-ea-fld"><label>Hora</label>
            <input type="time" value={cvHoraPago} onChange={(e) => setCvHoraPago(e.target.value)} /></div>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <div className="lxp-ea-cvhead">
            <span>Parcialidades</span>
            <div className="lxp-ea-spacer" />
            <button className="lxp-ea-btn sm" onClick={addParc}>＋ Agregar parcialidad</button>
          </div>
          <div className="lxp-ea-flist">
            {mparc.length ? mparc.map((p, i) => (
              <div key={i} className="lxp-ea-pr">
                <span className="pn">{i + 1}</span>
                <ScrollDateInput value={p.fecha} onChange={(v) => setParc(i, "fecha", v)} style={{ flex: 1, minWidth: 0 }} />
                <input type="time" style={{ flex: "0 0 92px" }} value={p.hora || "10:00"} onChange={(e) => setParc(i, "hora", e.target.value)} />
                <input type="text" inputMode="decimal" placeholder="$0.00" value={p.monto}
                  onChange={(e) => setParc(i, "monto", e.target.value)}
                  onBlur={(e) => setParc(i, "monto", e.target.value.trim() ? mny(numOf(e.target.value)) : "")} />
                <button className="x" onClick={() => rmParc(i)}>✕</button>
              </div>
            )) : <div style={{ fontSize: 12.5, color: "var(--lxp-ea-muted)" }}>Agrega la primera parcialidad.</div>}
          </div>
          {mparc.length > 0 && (
            <div className={`lxp-ea-calc ${Math.abs(cvDif) < 0.01 ? "grn" : "amb"}`}>
              <div className="b"><div className="k">Suma de parcialidades</div><div className="v">{mny(cvSuma)}</div></div>
              <div className="b"><div className="k">Diferencia contra el total</div>
                <div className="v" style={{ color: Math.abs(cvDif) < 0.01 ? "var(--lxp-ea-green)" : "var(--lxp-ea-amber)" }}>
                  {Math.abs(cvDif) < 0.01 ? "Cuadra" : mny(cvDif)}
                </div></div>
            </div>
          )}
        </div>
      )}

      <div className="lxp-ea-fld" style={{ marginTop: 14 }}><label>Notas del convenio</label>
        <textarea value={cvNotas} onChange={(e) => setCvNotas(e.target.value)} placeholder="Opcional" /></div>
    </div>
  );
  const resOpciones = resolviendo ? (RES[resolviendo.claseKey] || RES.preliminar) : RES.preliminar;
  const diasLimite = (kind === 0 || kind === 1) && nTerm && fecha ? fmt(addBusinessDays(fecha, nTerm)) : null;
  const modalTitle = editId ? "Editar actuación" : kind === 2 ? "Lo sucedido en la audiencia" : "Registrar actuación";

  if (loading) {
    return (
      <div className="lxp-ea" style={{ padding: 60, textAlign: "center", color: "var(--lxp-ea-muted)" }}>
        Cargando expediente…
      </div>
    );
  }

  return (
    <div className="lxp-ea">
      {/* ── Agenda de audiencias ── */}
      {proxima ? (() => {
        const d = difDias(hoyStr(), proxima.slot.fecha_audiencia);
        const cls = d < 0 ? "venc" : d === 0 ? "hoy" : "";
        const lbl = d < 0 ? "Audiencia vencida sin registrar" : d === 0 ? "Audiencia hoy" : "Próxima audiencia";
        return (
          <div className={`lxp-ea-agenda ${cls}`}>
            <div className="lxp-ea-agenda-in">
              <div>
                <div className="lxp-ea-ag-lbl">{lbl}</div>
                <div className="lxp-ea-ag-t">{proxima.meta.n}</div>
                <div className="lxp-ea-ag-s">{fmt(proxima.slot.fecha_audiencia)} · {fmtHora(proxima.slot.fecha_audiencia)} hrs</div>
              </div>
              <div className="lxp-ea-spacer" />
              <div className="lxp-ea-ag-cd">
                <div className="n">{Math.abs(d)}</div>
                <div className="u">{d < 0 ? "días de retraso" : d === 1 ? "día" : "días"}</div>
              </div>
              <div>
                <button className="lxp-ea-btn pri" onClick={(e) => { triggerHappyWave(e); abrirRegistrarSucedido(proxima.claseKey, proxima.idx); }}>
                  Registrar lo sucedido →
                </button>
              </div>
            </div>
          </div>
        );
      })() : hintJuicio ? (
        <div className="lxp-ea-agenda hint">
          <div className="lxp-ea-agenda-in">
            <div>
              <div className="lxp-ea-ag-lbl">Siguiente paso</div>
              <div className="lxp-ea-ag-t">Preliminar celebrada — falta agendar la audiencia de juicio</div>
              <div className="lxp-ea-ag-s">El tribunal debe señalarla dentro de los 10 días siguientes (art. 873-H LFT).</div>
            </div>
            <div className="lxp-ea-spacer" />
            <div><button className="lxp-ea-btn pri" onClick={() => nuevoSenalamiento("juicio")}>Agendar audiencia de juicio</button></div>
          </div>
        </div>
      ) : (
        <div className="lxp-ea-agenda none">
          <div className="lxp-ea-agenda-in">
            <div>
              <div className="lxp-ea-ag-lbl">Agenda</div>
              <div className="lxp-ea-ag-t">Sin audiencias señaladas</div>
              <div className="lxp-ea-ag-s">Cuando el tribunal te notifique una, agéndala aquí; su resultado se registra después.</div>
            </div>
            <div className="lxp-ea-spacer" />
            <div><button className="lxp-ea-btn pri" onClick={() => nuevoSenalamiento()}>Agendar audiencia</button></div>
          </div>
        </div>
      )}

      <EsquemaProcedimiento hechas={hechas} hechasFechas={hechasFechas} agendadasSet={agendadasSet} agendadasFechas={agendadasFechas} onEtapaClick={handleEtapaClick} />

      <div className="lxp-ea-cols">
        {/* ── Columna izquierda ── */}
        <div>
          <div className="lxp-ea-card">
            <div className="lxp-ea-card-h lxp-ea-act-head">
              <div className="lxp-ea-act-title">
                <h3>Actuaciones</h3>
                <span className="lxp-ea-act-sub">
                  {actuacionesVisibles.length + audienciaEventos.length} en total · más recientes primero
                </span>
              </div>
              <button className="lxp-ea-btn pri" onClick={(e) => { triggerHappyWave(e); abrirModal(); }}>＋ Registrar actuación</button>
            </div>
            <div className="lxp-ea-card-b">
              <div className="lxp-ea-chips lxp-ea-act-filters">
                {[["all", "Todo"], ["nos", "Despacho"], ["act", "Contraparte"], ["trib", "Tribunal"], ["aud", "Audiencias"]].map(([k, l]) => (
                  <button key={k} className={`lxp-ea-chip ${filtro === k ? "on" : ""}`} onClick={() => setFiltro(k)}>{l}</button>
                ))}
              </div>

              {nodosTimeline.length === 0 ? (
                <div className="lxp-ea-empty">
                  <div className="ic"><i className="fa-solid fa-file-lines" /></div>
                  <h4>Sin actuaciones registradas</h4>
                  <p>Este tab captura actuaciones y agenda audiencias.<br />
                    Carátula, antecedentes y terminación se alimentan desde sus propias secciones.</p>
                  <button className="lxp-ea-btn pri" onClick={(e) => { triggerHappyWave(e); abrirModal(); }}>Registrar la primera actuación</button>
                </div>
              ) : visibles.length === 0 ? (
                <div className="lxp-ea-empty"><p>Ninguna actuación con ese filtro.</p></div>
              ) : (
                <div className="lxp-ea-tl2">
                  {visiblesDesc.map((n, dispIdx) => {
                    const isLastRow = dispIdx === visiblesDesc.length - 1;

                    if (n.isVirtual) {
                      const d = difDias(hoyStr(), n.slot.fecha_audiencia);
                      return (
                        <div
                          key={`v-${n.claseKey}-${n.idx}`}
                          id={n.meta.esq != null ? `lxp-ea-node-${n.meta.esq}` : undefined}
                          className={`lxp-ea-tlrow ${highlightEsq === n.meta.esq ? "lxp-ea-highlight" : ""}`}
                        >
                          <div className="lxp-ea-tlrail">
                            <div className="dot ag">◷</div>
                            {!isLastRow && <div className="ln" />}
                          </div>
                          <div
                            className="lxp-ea-tlcard agenda-node lxp-ea-clickable"
                            onClick={(e) => { if (e.target.closest("button, a, input, textarea, select")) return; setVerNode(n); }}
                          >
                            <div className="lxp-ea-ev-head">
                              <span className="t">{n.meta.n}</span>
                              <span className="lxp-ea-who ag">Agendada</span>
                              {n.meta.art && <span className="lxp-ea-art">{n.meta.art}</span>}
                              <span className="lxp-ea-spacer" />
                              <span className="lxp-ea-acts">
                                <button className="lxp-ea-btn lxp-ea-ghost sm" onClick={() => setVerNode(n)}>Ver</button>
                                <button
                                  className="lxp-ea-btn lxp-ea-ghost danger sm"
                                  onClick={() => eliminarAudienciaSlot(n.meta.etapa, n.idx, n.meta.n, n.slot.fecha_audiencia)}
                                >
                                  Eliminar
                                </button>
                              </span>
                              <span className="d">{fmt(n.slot.fecha_audiencia)} · {fmtHora(n.slot.fecha_audiencia)} hrs</span>
                            </div>
                            <div className="lxp-ea-agbox">
                              <div className="l">Pendiente de celebrarse</div>
                              <div style={{ fontSize: 12.5, color: "#475569" }}>
                                {d < 0 ? `Debió celebrarse hace ${Math.abs(d)} días.` : d === 0 ? "Es hoy." : `Faltan ${d} días.`}
                              </div>
                              <button className="lxp-ea-btn pri sm" style={{ marginTop: 9 }} onClick={(e) => { triggerHappyWave(e); abrirRegistrarSucedido(n.claseKey, n.idx); }}>
                                Registrar lo sucedido
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    const isAud = !!n.isAudiencia;
                    const isTerm = !!n.isTerminacion;
                    const docs = n.documentos || [];
                    const esConvEvento = isAud && /convenio/i.test(n.resultado || "");
                    const esqIdx = ESQ_INDEX[isAud ? n.tipo.replace(AUDIENCIA_DOC_PREFIX, "") : n.tipo];
                    const nodeKey = n.id_tribunal_actuacion || n.id;
                    const isUltima = dispIdx === 0;
                    const docsOpen = !!docsAbiertos[nodeKey];
                    const docsLabel =
                      docs.length === 1 && n.autor === "nos" && n.estado
                        ? `1 archivo · ${ESTADO_LBL[n.estado] || "Presentado"}`
                        : `${docs.length} archivo${docs.length === 1 ? "" : "s"}`;

                    return (
                      <div
                        key={nodeKey}
                        id={esqIdx != null ? `lxp-ea-node-${esqIdx}` : undefined}
                        className={`lxp-ea-tlrow ${highlightEsq === esqIdx ? "lxp-ea-highlight" : ""}`}
                      >
                        <div className="lxp-ea-tlrail">
                          <div className={`dot ${isTerm ? "term" : isUltima ? "last" : "done"}`}>
                            {isTerm ? <i className="fa-solid fa-flag-checkered" /> : n._seq}
                          </div>
                          {!isLastRow && <div className="ln" />}
                        </div>
                        <div
                          className={`lxp-ea-tlcard lxp-ea-clickable ${isUltima ? "last" : ""} ${isTerm ? "term" : ""}`}
                          onClick={(e) => { if (e.target.closest("button, a, input, textarea, select")) return; setVerNode(n); }}
                        >
                          <div className="lxp-ea-ev-head">
                            <span className="t">{n.tipo}</span>
                            {!isTerm && <span className={`lxp-ea-who ${n.autor}`}>{WHO[n.autor]}</span>}
                            {isAud && <span className={`lxp-ea-st ${norm(n.resultado).replace(/\s+/g, "-")}`}>{n.resultado}</span>}
                            {isTerm && <span className="lxp-ea-st celebrada">Terminado</span>}
                            {n.fundamento && <span className="lxp-ea-art">{n.fundamento}</span>}
                            <span className="lxp-ea-spacer" />
                            <span className="lxp-ea-acts">
                              {isTerm ? (
                                onIrATerminacion && (
                                  <button className="lxp-ea-btn lxp-ea-ghost sm" onClick={onIrATerminacion}>Ver / editar</button>
                                )
                              ) : (
                                <>
                                  <button className="lxp-ea-btn lxp-ea-ghost sm" onClick={() => setVerNode(n)}>Ver</button>
                                  {!isAud && !n.isDemandaAuto && (
                                    <button className="lxp-ea-btn lxp-ea-ghost sm" onClick={() => editarActuacion(n)}>Editar</button>
                                  )}
                                  {isAud && (
                                    <button className="lxp-ea-btn lxp-ea-ghost sm" onClick={() => editarAudienciaSucedido(n)}>Editar</button>
                                  )}
                                  {n.isDemandaAuto ? null : isAud ? (
                                    <button
                                      className="lxp-ea-btn lxp-ea-ghost danger sm"
                                      onClick={() => eliminarAudienciaSlot(n.etapa, n.slotIdx, n.tipo.replace(AUDIENCIA_DOC_PREFIX, ""), n.fecha)}
                                    >
                                      Eliminar
                                    </button>
                                  ) : (
                                    <button className="lxp-ea-btn lxp-ea-ghost danger sm" onClick={() => eliminarActuacion(n)}>Eliminar</button>
                                  )}
                                </>
                              )}
                            </span>
                            <span className="d">{fmt(n.fecha)}</span>
                          </div>

                          {isUltima && <div className="lxp-ea-ultima">Última actuación</div>}

                          {n.term_dias ? (
                            <div className="lxp-ea-venc-row">
                              <span className="lxp-ea-venc-chip"><i className="fa-regular fa-calendar" /> Vence {fmt(n.fecha_limite)}</span>
                              <span className="lxp-ea-venc-term">Término: {n.term_dias} días hábiles</span>
                            </div>
                          ) : null}

                          {n.notas && <div className="lxp-ea-nota">{n.notas}</div>}

                          {isAud && n.pruebasResultado?.length > 0 && (
                            <div className="lxp-ea-pdtbl-wrap">
                              <div className="k" style={{ marginBottom: 6 }}>Pruebas — admisión</div>
                              <table className="lxp-ea-pdtbl">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Parte</th>
                                    <th>Prueba</th>
                                    <th>Detalle</th>
                                    <th>Resultado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {n.pruebasResultado.map((p, pIdx) => {
                                    const tipoLbl = TIPO_PRUEBA_DEMANDADA_OPTS.find((o) => o.value === p.tipo)?.label || p.tipo;
                                    const resumen = p.resumen || resumenPruebaDemandada(p);
                                    const admitida = p.resultado === "admitida";
                                    const esActora = p.parte === "actora";
                                    return (
                                      <tr key={pIdx}>
                                        <td>{pIdx + 1}</td>
                                        <td>{esActora ? "Actora" : "Demandada"}</td>
                                        <td><b>{tipoLbl}</b></td>
                                        <td className="m">{resumen || "—"}</td>
                                        <td>
                                          <span className={`lxp-ea-st ${admitida ? "celebrada" : "sin-conciliacion"}`}>
                                            {admitida ? "Admitida" : "Desechada"}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {docs.length > 0 && (
                            <>
                              <button type="button" className="lxp-ea-doctoggle" onClick={() => toggleDocsAbiertos(nodeKey)}>
                                <i className={`fa-solid fa-chevron-${docsOpen ? "down" : "right"}`} />
                                {docsLabel}
                              </button>
                              {docsOpen && (
                                <div className="lxp-ea-doclist">
                                  {docs.map((doc) => (
                                    <div key={doc.id} className="lxp-ea-docrow" onClick={() => abrirDoc(doc)}>
                                      <span className="lxp-ea-doclabel">{ROL[doc.rol] || "Doc"}</span>
                                      <span className="lxp-ea-docname">{doc.filename}</span>
                                      <button
                                        type="button"
                                        className="lxp-ea-doc-del"
                                        title="Eliminar documento"
                                        onClick={(e) => eliminarDoc(e, doc)}
                                      >
                                        <i className="fa-solid fa-trash" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {esConvEvento && (
                            <ConvenioBox
                              term={terminacion}
                              onConstancia={abrirConstancia}
                              onVerDoc={abrirDoc}
                              onIrATerminacion={onIrATerminacion}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Columna derecha ── */}
        <div>
          <div className="lxp-ea-card">
            <div className="lxp-ea-card-h">
              <div className="lxp-ea-hgroup">
                <span className="lxp-ea-icon-badge"><i className="fa-solid fa-calendar-days" /></span>
                <h3>Audiencias</h3>
              </div>
              <span className="lxp-ea-cnt">{audienciaEventos.length + agendaNodes.length}</span>
            </div>
            <div className="lxp-ea-card-b">
              {[...agendaNodes.map((n) => ({ n: n.meta.n, f: n.slot.fecha_audiencia, st: "agendada", etapa: n.meta.etapa, idx: n.idx })),
              ...audienciaEventos.map((n) => ({ n: n.tipo.replace("Audiencia celebrada — ", ""), f: n.fecha, st: norm(n.resultado), etapa: n.etapa, idx: n.slotIdx }))]
                .sort((a, b) => new Date(a.f) - new Date(b.f))
                .map((a, i) => {
                  const col = a.st === "agendada" ? "var(--lxp-ea-violet)" : a.st === "diferida" ? "var(--lxp-ea-amber)" : "var(--lxp-ea-green)";
                  return (
                    <div key={i} className="lxp-ea-aud-row">
                      <span className="dt" style={{ background: col }} />
                      <span className="n" data-full={a.n}>{a.n}</span>
                      <span className="f">{fmt(a.f)}{fmtHora(a.f) ? ` · ${fmtHora(a.f)}` : ""}</span>
                      <button
                        className="lxp-ea-aud-del"
                        title="Eliminar audiencia"
                        onClick={() => eliminarAudienciaSlot(a.etapa, a.idx, a.n, a.f)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  );
                })}
              {audienciaEventos.length + agendaNodes.length === 0 && (
                <div style={{ fontSize: 12.5, color: "var(--lxp-ea-muted)" }}>Sin audiencias registradas.</div>
              )}
            </div>
          </div>

          <div className="lxp-ea-card">
            <div className="lxp-ea-card-h">
              <div className="lxp-ea-hgroup">
                <span className="lxp-ea-icon-badge"><i className="fa-solid fa-gauge-high" /></span>
                <h3>Avance del procedimiento</h3>
              </div>
              <span className="lxp-ea-cnt">{hechas.size}/{ESQ.length}</span>
            </div>
            <div className="lxp-ea-card-b">
              <div className="lxp-ea-prog"><i style={{ width: `${(hechas.size / ESQ.length) * 100}%` }} /></div>
              <div style={{ fontSize: 11.5, color: "var(--lxp-ea-muted)", marginBottom: 12 }}>Se marca solo con lo que registres aquí.</div>
              <div className="lxp-ea-mini">
                {ESQ.map((t, i) => (
                  <React.Fragment key={i}>
                    {ESQG[i] && <div className="grp">{ESQG[i]}</div>}
                    <div className={`s ${hechas.has(i) ? "done" : agendadasSet.has(i) ? "ag" : ""}`}>
                      <i />{t}{agendadasSet.has(i) && !hechas.has(i) && <span className="n">agendada</span>}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          <div className="lxp-ea-card">
            <div className="lxp-ea-card-h">
              <div className="lxp-ea-hgroup">
                <span className="lxp-ea-icon-badge"><i className="fa-solid fa-file-pen" /></span>
                <h3>Escritos del despacho</h3>
              </div>
              <span className="lxp-ea-cnt">{escritosDespacho.length}</span>
            </div>
            <div className="lxp-ea-card-b">
              {escritosDespacho.length ? escritosDespacho.map((a) => (
                <div key={a.id_tribunal_actuacion} className="lxp-ea-aud-row">
                  <span className="dt" style={{ background: "var(--lxp-ea-accent)" }} />
                  <span className="n">{a.tipo}</span>
                  <span className="f">{fmt(a.fecha)}</span>
                </div>
              )) : <div style={{ fontSize: 12.5, color: "var(--lxp-ea-muted)" }}>Aún no registras escritos del despacho.</div>}
            </div>
          </div>

          {antecedentes && (
            <div className="lxp-ea-card">
              <div className="lxp-ea-card-h">
                <div className="lxp-ea-hgroup">
                  <span className="lxp-ea-icon-badge"><i className="fa-solid fa-clock-rotate-left" /></span>
                  <h3>Antecedentes</h3>
                </div>
                <span className="lxp-ea-ro">Otra sección</span>
              </div>
              <div className="lxp-ea-card-b">
                <div className="lxp-ea-kv"><span>Último día laborado</span><b>{fmt(ant.ultimoDiaLaborado)}</b></div>
                <div className="lxp-ea-kv"><span>Emisión del citatorio (CCL)</span><b>{fmt(ant.citatorioCCL)}</b></div>
                <div className="lxp-ea-kv"><span>Constancia de no conciliación</span><b>{fmt(ant.constanciaNoConciliacion)}</b></div>
                <div className="lxp-ea-kv"><span>Presentación de demanda</span><b>{fmt(ant.presentacionDemanda)}</b></div>
                <div className="lxp-ea-kv lxp-ea-kv-top">
                  <span style={{ fontWeight: 650, color: "var(--lxp-ea-ink)" }}>Días transcurridos</span>
                  <b style={{ color: "var(--lxp-ea-green)" }}>{antDias}</b>
                </div>
              </div>
            </div>
          )}

          <div className="lxp-ea-card">
            <div className="lxp-ea-card-h">
              <div className="lxp-ea-hgroup">
                <span className="lxp-ea-icon-badge"><i className="fa-solid fa-layer-group" /></span>
                <h3>Resumen</h3>
              </div>
            </div>
            <div className="lxp-ea-card-b">
              <div className="lxp-ea-kv"><span>Escritos del despacho</span><b>{countBy("nos")}</b></div>
              <div className="lxp-ea-kv"><span>Actuaciones del tribunal</span><b>{countBy("trib")}</b></div>
              <div className="lxp-ea-kv"><span>Actuaciones de la contraparte</span><b>{countBy("act")}</b></div>
              <div className="lxp-ea-kv"><span>Documentos cargados</span><b>{totalDocs}</b></div>
              <div className="lxp-ea-kv lxp-ea-kv-top">
                <span style={{ fontWeight: 650, color: "var(--lxp-ea-ink)" }}>Terminación</span>
                <b style={{ color: terminacion ? "var(--lxp-ea-green)" : undefined }}>{terminacionLbl}</b>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal registrar / editar actuación ── */}
      <Overlay
        open={modalOpen}
        onClose={cerrarModal}
        maxWidth={
          norm(sel) === "contestacion de demanda" ? 920
            : kind === 2 && resolviendo?.claseKey === "preliminar" ? 920
              : 780
        }
      >
        <div className="lxp-ea-m-h">
          <div>
            <h3 className="lxp-ea-m-title">{modalTitle}</h3>
            <div className="lxp-ea-mh-s">{step === 1 ? "Paso 1 — elige la actuación" : "Paso 2 — detalle"}</div>
          </div>
          <div className="lxp-ea-steps">
            <span className={step === 1 ? "on" : ""}><i>1</i>Actuación</span>
            <i className="sep" />
            <span className={step === 2 ? "on" : ""}><i>2</i>Detalle</span>
          </div>
          <button className="lxp-ea-x" onClick={cerrarModal} aria-label="Cerrar">✕</button>
        </div>
        <div className="lxp-ea-m-b">
        {step === 1 && (
          <div>
            <input className="lxp-ea-search" placeholder="Buscar actuación…" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
            {filtered.map((grp) => (
              <div key={grp.g}>
                <div className="lxp-ea-catgrp">{grp.g}</div>
                <div className="lxp-ea-sugg">
                  {grp.items.map((c) => (
                    <button key={c.n} className={`lxp-ea-sg ${c.kind ? "aud" : ""}`} onClick={() => pick(c)}>
                      <span className="t">{c.n}</span>
                      <span className="f">{c.kind === 1 ? <b>Agendar</b> : c.term ? <><b>{c.term} días</b> · {c.fund}</> : c.fund}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--lxp-ea-muted)", fontSize: 12.5 }}>Sin resultados.</div>}
          </div>
        )}

        {step === 2 && (
          <div>
            {/* picked */}
            <div className={`lxp-ea-picked ${kind ? "aud" : ""}`}>
              <div>
                <div className="t">
                  {kind === 1 ? "Agendar audiencia" : kind === 2 ? (resolviendo ? CLASE[resolviendo.claseKey].n : "Registrar lo sucedido") : sel}
                </div>
                <div className="f">
                  {kind === 2 && resolviendo
                    ? `Señalada para el ${fmt(resolviendo.fechaSenalada)} · ${fmtHora(resolviendo.fechaSenalada)} hrs`
                    : (selCat?.fund || catByName(sel)?.fund || "Sin fundamento específico")}
                </div>
              </div>
              <div className="lxp-ea-spacer" />
              {!editId && kind !== 2 && <button className="lxp-ea-btn sm" onClick={() => setStep(1)}>Cambiar</button>}
            </div>

            {/* kind 1: agendar */}
            {kind === 1 && (
              <>
                <div className="lxp-ea-secl">Agendar la audiencia</div>
                <div className="lxp-ea-grid3">
                  <div className="lxp-ea-fld">
                    <label>Clase de audiencia</label>
                    <select value={mClase} onChange={(e) => setMClase(e.target.value)}>
                      {Object.entries(CLASE).map(([k, v]) => <option key={k} value={k}>{v.n}</option>)}
                    </select>
                  </div>
                  <div className="lxp-ea-fld"><label>Fecha de la audiencia</label>
                    <ScrollDateInput value={mAudF} onChange={setMAudF} style={{ width: "100%" }} /></div>
                  <div className="lxp-ea-fld"><label>Hora</label>
                    <input type="time" value={mAudH} onChange={(e) => setMAudH(e.target.value)} /></div>
                </div>
                {mAudF && (() => {
                  const d = difDias(hoyStr(), mAudF);
                  const faltanTxt = d < 0 ? `${Math.abs(d)} días atrás` : d === 0 ? "Es hoy" : `${d} días`;
                  return (
                    <div className="lxp-ea-calc vio">
                      <div className="b"><div className="k">Faltan</div><div className="v" style={{ color: "var(--lxp-ea-violet)" }}>{faltanTxt}</div></div>
                      <div className="b"><div className="k">Se celebrará</div><div className="v">{fmt(mAudF)} · {mAudH} hrs</div></div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* kind 2: lo sucedido */}
            {kind === 2 && (
              <>
                <div className="lxp-ea-secl">Lo sucedido en la audiencia</div>
                <div className="lxp-ea-grid2">
                  <div className="lxp-ea-fld">
                    <label>Resultado</label>
                    <select value={mRes} onChange={(e) => setMRes(e.target.value)}>
                      <option value="">Selecciona resultado</option>
                      {resOpciones.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="lxp-ea-fld"><label>Fecha en que se celebró</label>
                    <ScrollDateInput value={mFechaAud} onChange={setMFechaAud} style={{ width: "100%" }} /></div>
                </div>

                {resolviendo?.claseKey === "preliminar" && pruebasContestacionDemandada.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div className="lxp-ea-secl">Pruebas de la demandada — admisión</div>
                    <div className="lxp-ea-pdem-list">
                      {pruebasContestacionDemandada.map((p, idx) => {
                        const tipoLbl = TIPO_PRUEBA_DEMANDADA_OPTS.find((o) => o.value === p.tipo)?.label || p.tipo;
                        const resumen = resumenPruebaDemandada(p);
                        const veredicto = pruebasVeredicto[p.key] || null;
                        return (
                          <div key={p.key} className="lxp-ea-pdem-row open">
                            <div className="lxp-ea-pdem-sum" style={{ cursor: "default" }}>
                              <span className="lxp-ea-pdem-n">{idx + 1}</span>
                              <span className="lxp-ea-pdem-sum-txt full">
                                <b>{tipoLbl}</b>
                                {resumen && <span className="m"> — {resumen}</span>}
                              </span>
                            </div>
                            <div className="lxp-ea-pdem-chipgrid" style={{ padding: "0 12px 12px" }}>
                              <button
                                type="button"
                                className={`lxp-ea-pdem-chk ${veredicto === "admitida" ? "on" : ""}`}
                                onClick={() => setPruebasVeredicto((prev) => ({ ...prev, [p.key]: "admitida" }))}
                                aria-pressed={veredicto === "admitida"}
                              >
                                <span className="bx">{veredicto === "admitida" && "✓"}</span>
                                <span className="lb">Admitida</span>
                              </button>
                              <button
                                type="button"
                                className={`lxp-ea-pdem-chk ${veredicto === "desechada" ? "on" : ""}`}
                                onClick={() => setPruebasVeredicto((prev) => ({ ...prev, [p.key]: "desechada" }))}
                                aria-pressed={veredicto === "desechada"}
                              >
                                <span className="bx">{veredicto === "desechada" && "✓"}</span>
                                <span className="lb">Desechada</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {resolviendo?.claseKey === "preliminar" && pruebasContestacionActora.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div className="lxp-ea-secl">Pruebas de la actora — admisión</div>
                    <div className="lxp-ea-pdem-list">
                      {pruebasContestacionActora.map((p, idx) => {
                        const tipoLbl = TIPO_PRUEBA_DEMANDADA_OPTS.find((o) => o.value === p.tipo)?.label || p.tipo;
                        const veredicto = pruebasVeredicto[p.key] || null;
                        return (
                          <div key={p.key} className="lxp-ea-pdem-row open">
                            <div className="lxp-ea-pdem-sum" style={{ cursor: "default" }}>
                              <span className="lxp-ea-pdem-n">{idx + 1}</span>
                              <span className="lxp-ea-pdem-sum-txt full">
                                <b>{tipoLbl}</b>
                                {p.resumen && <span className="m"> — {p.resumen}</span>}
                              </span>
                            </div>
                            <div className="lxp-ea-pdem-chipgrid" style={{ padding: "0 12px 12px" }}>
                              <button
                                type="button"
                                className={`lxp-ea-pdem-chk ${veredicto === "admitida" ? "on" : ""}`}
                                onClick={() => setPruebasVeredicto((prev) => ({ ...prev, [p.key]: "admitida" }))}
                                aria-pressed={veredicto === "admitida"}
                              >
                                <span className="bx">{veredicto === "admitida" && "✓"}</span>
                                <span className="lb">Admitida</span>
                              </button>
                              <button
                                type="button"
                                className={`lxp-ea-pdem-chk ${veredicto === "desechada" ? "on" : ""}`}
                                onClick={() => setPruebasVeredicto((prev) => ({ ...prev, [p.key]: "desechada" }))}
                                aria-pressed={veredicto === "desechada"}
                              >
                                <span className="bx">{veredicto === "desechada" && "✓"}</span>
                                <span className="lb">Desechada</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/diferida/i.test(mRes) && (
                  <div style={{ marginTop: 16 }}>
                    <div className="lxp-ea-secl">Nueva fecha señalada</div>
                    <div className="lxp-ea-grid2">
                      <div className="lxp-ea-fld"><label>Fecha</label>
                        <ScrollDateInput value={mDifF} onChange={setMDifF} style={{ width: "100%" }} /></div>
                      <div className="lxp-ea-fld"><label>Hora</label>
                        <input type="time" value={mDifH} onChange={(e) => setMDifH(e.target.value)} /></div>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--lxp-ea-muted)", marginTop: 8 }}>Se agenda automáticamente una nueva audiencia de la misma clase.</div>
                  </div>
                )}

                {esConvenioModal && convenioCamposBox}
              </>
            )}

            {/* kind 0: común · kind 1: notificación (solo fecha) · kind 2: datos del registro (solo fecha) */}
            {(kind === 0 || kind === 1 || kind === 2) && (
              <>
                {showWho && (
                  <>
                    <div className="lxp-ea-secl">¿Quién lo promueve?</div>
                    <div className="lxp-ea-who3 dos">
                      {WHO3.filter((w) => w[0] !== "trib").map(([k, t, s]) => (
                        <button key={k} className={`lxp-ea-wc ${autor === k ? "on" : ""}`} data-w={k} onClick={() => setAutor(k)}>
                          <div className="d" /><div className="t">{t}</div><div className="s">{s}</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="lxp-ea-secl">{kind === 1 ? "Notificación" : "Datos"}</div>
                <div className="lxp-ea-grid3">
                  <div className="lxp-ea-fld"><label>{lbFecha}</label>
                    <ScrollDateInput value={fecha} onChange={setFecha} style={{ width: "100%" }} /></div>
                  {kind === 0 && (
                    <div className="lxp-ea-fld"><label>Días hábiles del término</label>
                      <select value={term} onChange={(e) => setTerm(Number(e.target.value))}>
                        {TERM_OPCIONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select></div>
                  )}
                  {showEstado && (
                    <div className="lxp-ea-fld"><label>Estado del escrito <span className="lxp-ea-new">nuevo</span></label>
                      <select value={estado} onChange={(e) => setEstado(e.target.value)}>
                        <option value="borrador">Borrador</option>
                        <option value="revision">En revisión</option>
                        <option value="presentado">Presentado</option>
                        <option value="acordado">Acordado</option>
                      </select></div>
                  )}
                </div>
                {diasLimite && (
                  <div className="lxp-ea-calc">
                    <div className="b"><div className="k">Vencimiento del término</div><div className="v" style={{ color: "var(--lxp-ea-amber)" }}>{diasLimite}</div></div>
                  </div>
                )}

                {esConvenioActuacion && convenioCamposBox}

                {kind === 0 && sel === "Requerimiento o prevención" && (
                  <div style={{ marginBottom: 20 }}>
                    <div className="lxp-ea-secl" style={{ marginTop: 28 }}>Cumplimiento del requerimiento</div>
                    <div className="lxp-ea-fld" style={{ marginBottom: 14 }}>
                      <label>Fecha de cumplimiento</label>
                      <ScrollDateInput value={fechaCumplimiento} onChange={setFechaCumplimiento} style={{ width: "100%" }} />
                    </div>
                    <div className="lxp-ea-dz" style={{ marginBottom: 10 }}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("over"); }}
                      onDragLeave={(e) => e.currentTarget.classList.remove("over")}
                      onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("over"); addCumplFiles(e.dataTransfer.files); }}
                      onClick={() => cumplInputRef.current?.click()}>
                      <b>Arrastra los archivos o haz clic para seleccionar</b>
                      <span>Documento con el que se cumple el requerimiento</span>
                    </div>
                    <input ref={cumplInputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }}
                      onChange={(e) => { addCumplFiles(e.target.files); e.target.value = ""; }} />
                    {cumplFiles.length > 0 && (
                      <div className="lxp-ea-flist">
                        {cumplFiles.map((f, i) => (
                          <div key={i} className="lxp-ea-fr">
                            <span className="nm">{f.name}</span>
                            <button className="x" onClick={() => rmCumplFile(i)}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {kind === 0 && sel === "Contestación de demanda" && (
                  <>
                    <div className="lxp-ea-secl">
                      Pruebas de la demandada{pruebasDemandada.length > 0 ? ` (${pruebasDemandada.length})` : ""}
                    </div>
                    <div className="lxp-ea-pdem">
                      {pruebasDemandada.length > 0 && (
                        <div className="lxp-ea-pdem-list">
                          {pruebasDemandada.map((p, idx) => {
                            const abierto = pdemAbiertoId === p.id;
                            const tipoLbl = TIPO_PRUEBA_DEMANDADA_OPTS.find((o) => o.value === p.tipo)?.label;
                            const resumen = p.tipo ? resumenPruebaDemandada(p) : "";
                            return (
                              <div key={p.id} className={`lxp-ea-pdem-row ${abierto ? "open" : ""}`}>
                                <button
                                  type="button"
                                  className="lxp-ea-pdem-sum"
                                  onClick={() => setPdemAbiertoId(abierto ? null : p.id)}
                                  aria-expanded={abierto}
                                >
                                  <span className="lxp-ea-pdem-n">{idx + 1}</span>
                                  <span className="lxp-ea-pdem-sum-txt">
                                    <b>{tipoLbl || "Selecciona el tipo de prueba"}</b>
                                    {resumen && <span className="m"> — {resumen}</span>}
                                  </span>
                                  <span className="lxp-ea-pdem-chev">{abierto ? "▾" : "▸"}</span>
                                </button>
                                <button
                                  className="x"
                                  onClick={(e) => { e.stopPropagation(); removePruebaDemandada(p.id); }}
                                  title="Eliminar prueba"
                                >
                                  ✕
                                </button>

                                {abierto && (
                                  <div className="lxp-ea-pdem-body">
                                    <div className="lxp-ea-fld lxp-ea-pdem-tiposel">
                                      <label>Tipo de prueba</label>
                                      <select
                                        value={p.tipo}
                                        onChange={(e) => setPruebaDemandadaTipo(p.id, e.target.value)}
                                      >
                                        <option value="">Selecciona el tipo de prueba</option>
                                        {TIPO_PRUEBA_DEMANDADA_OPTS.map((o) => (
                                          <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                      </select>
                                    </div>

                                    {p.tipo ? (
                                      <div className="lxp-ea-pdem-fields">
                                        {DEMANDADA_CAMPOS_POR_TIPO[p.tipo].map((campo) => (
                                          <div
                                            key={campo.key}
                                            className={`lxp-ea-fld lxp-ea-pdem-fld ${campo.span === "full" ? "full" : ""}`}
                                          >
                                            <label>{campo.label}</label>
                                            {campo.type === "textarea" ? (
                                              <textarea
                                                value={p.campos?.[campo.key] || ""}
                                                placeholder={campo.placeholder}
                                                onChange={(e) => setPruebaDemandadaCampo(p.id, campo.key, e.target.value)}
                                              />
                                            ) : campo.type === "date" ? (
                                              <ScrollDateInput
                                                value={p.campos?.[campo.key] || ""}
                                                onChange={(v) => setPruebaDemandadaCampo(p.id, campo.key, v)}
                                                style={{ width: "100%" }}
                                              />
                                            ) : campo.type === "chips" ? (
                                              <div className="lxp-ea-pdem-chipgrid">
                                                {campo.options.map((o) => {
                                                  const on = (p.campos?.[campo.key] || []).includes(o.value);
                                                  return (
                                                    <button
                                                      type="button"
                                                      key={o.value}
                                                      className={`lxp-ea-pdem-chk ${on ? "on" : ""}`}
                                                      onClick={() => toggleChipPruebaDemandada(p.id, campo.key, o.value)}
                                                      aria-pressed={on}
                                                    >
                                                      <span className="bx">{on && "✓"}</span>
                                                      <span className="lb">{o.label}</span>
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <input
                                                type="text"
                                                value={p.campos?.[campo.key] || ""}
                                                placeholder={campo.placeholder}
                                                onChange={(e) => setPruebaDemandadaCampo(p.id, campo.key, e.target.value)}
                                              />
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="lxp-ea-pdem-hint">Elige un tipo para capturar su información.</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <button type="button" className="lxp-ea-btn sm" onClick={addPruebaDemandada}>
                        + Agregar prueba de la demandada
                      </button>
                    </div>

                    <div className="lxp-ea-fld" style={{ marginTop: 16 }}>
                      <label>Defensa de la demandada</label>
                      <textarea
                        value={defensaDemandada}
                        onChange={(e) => setDefensaDemandada(e.target.value)}
                        placeholder="Opcional"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Documentos (todos los kinds) */}
            <div className="lxp-ea-secl">Documentos</div>
            <div className="lxp-ea-dz"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("over"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("over")}
              onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("over"); addFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}>
              <b>{kind === 1 ? "Cédula o auto que señala la audiencia" : "Arrastra los archivos o haz clic para seleccionar"}</b>
              <span>{kind === 2 ? "Acta de audiencia, autos, anexos" : "Puedes cargar varios a la vez"}</span>
            </div>
            <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            {pendingFiles.length > 0 && (
              <div className="lxp-ea-flist">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="lxp-ea-fr">
                    <span className="nm">{f.name}</span>
                    <select value={f.rol} onChange={(e) => setRolFile(i, e.target.value)}>
                      {Object.keys(ROL).map((r) => <option key={r} value={r}>{ROL[r]}</option>)}
                    </select>
                    <button className="x" onClick={() => rmFile(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            <div className="lxp-ea-fld" style={{ marginTop: 16 }}>
              <label>Notas</label>
              <textarea value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Opcional" />
            </div>
          </div>
        )}
        </div>
        <div className="lxp-ea-m-f">
          {editId && <button className="lxp-ea-btn danger sm" onClick={eliminarDesdeModal}>Eliminar</button>}
          {step === 2 && !editId && kind !== 2 && <button className="lxp-ea-btn sm" onClick={() => setStep(1)}>← Atrás</button>}
          <div className="lxp-ea-spacer" />
          <button className="lxp-ea-btn" onClick={cerrarModal}>Cancelar</button>
          <button className="lxp-ea-btn pri" disabled={step !== 2 || saving} onClick={(e) => { triggerHappyWave(e); guardar(); }}>
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </Overlay>

      {/* ── Modal constancia de cumplimiento ── */}
      <Overlay open={constOpen} onClose={() => setConstOpen(false)} maxWidth={470}>
        <div className="lxp-ea-m-h">
          <div>
            <h3 className="lxp-ea-m-title">Constancia de cumplimiento</h3>
            <div className="lxp-ea-mh-s">La emite el tribunal y con ella concluye el expediente</div>
          </div>
          <button className="lxp-ea-x" onClick={() => setConstOpen(false)} aria-label="Cerrar">✕</button>
        </div>
        <div className="lxp-ea-m-b">
          <div className="lxp-ea-fld"><label>Fecha de la constancia</label>
            <ScrollDateInput value={ccFecha} onChange={setCcFecha} style={{ width: "100%" }} /></div>
          <div className="lxp-ea-secl">Documento</div>
          <div className="lxp-ea-dz"
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("over"); }}
            onDragLeave={(e) => e.currentTarget.classList.remove("over")}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("over"); setCcFile(e.dataTransfer.files?.[0] || null); }}
            onClick={() => ccInputRef.current?.click()}>
            <b>Adjunta la constancia</b>
            <span>{ccFile ? ccFile.name : "Auto o constancia emitida por el tribunal"}</span>
          </div>
          <input ref={ccInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }}
            onChange={(e) => { setCcFile(e.target.files?.[0] || null); e.target.value = ""; }} />
          <div className="lxp-ea-fld" style={{ marginTop: 16 }}><label>Notas</label>
            <textarea value={ccNotas} onChange={(e) => setCcNotas(e.target.value)} placeholder="Opcional" /></div>
        </div>
        <div className="lxp-ea-m-f">
          <div className="lxp-ea-spacer" />
          <button className="lxp-ea-btn" onClick={() => setConstOpen(false)}>Cancelar</button>
          <button className="lxp-ea-btn pri" disabled={savingConst} onClick={(e) => { triggerHappyWave(e); guardarConstancia(); }}>
            {savingConst ? "Guardando…" : "Guardar constancia"}
          </button>
        </div>
      </Overlay>

      {/* ── Vista ampliada de una actuación / audiencia de la línea del tiempo ── */}
      <Overlay open={!!verNode} onClose={() => setVerNode(null)} maxWidth={720}>
        {verNode && (() => {
          const n = verNode;
          const isVirtual = !!n.isVirtual;
          const isAud = !!n.isAudiencia;
          const isTerm = !!n.isTerminacion;
          const titulo = isVirtual ? n.meta.n : n.tipo;
          const fundamento = isVirtual ? n.meta.art : n.fundamento;
          const fechaMostrar = isVirtual ? n.slot.fecha_audiencia : n.fecha;
          const docs = isVirtual ? [] : (n.documentos || []);
          const esConvEvento = isAud && /convenio/i.test(n.resultado || "");
          return (
            <>
              <div className="lxp-ea-m-h">
                <div>
                  <h3 className="lxp-ea-m-title">{titulo}</h3>
                  <div className="lxp-ea-mh-s">
                    {isVirtual ? "Audiencia agendada" : isTerm ? "Terminación del expediente" : isAud ? "Audiencia celebrada" : WHO[n.autor] || ""}
                  </div>
                </div>
                <button className="lxp-ea-x" onClick={() => setVerNode(null)} aria-label="Cerrar">✕</button>
              </div>
              <div className="lxp-ea-m-b">
                <div className="lxp-ea-ev-head">
                  {!isVirtual && !isTerm && <span className={`lxp-ea-who ${n.autor}`}>{WHO[n.autor]}</span>}
                  {isVirtual && <span className="lxp-ea-who ag">Agendada</span>}
                  {isAud && <span className={`lxp-ea-st ${norm(n.resultado).replace(/\s+/g, "-")}`}>{n.resultado}</span>}
                  {isTerm && <span className="lxp-ea-st celebrada">Terminado</span>}
                  {fundamento && <span className="lxp-ea-art">{fundamento}</span>}
                  <span className="lxp-ea-spacer" />
                  <span className="d">{fmt(fechaMostrar)}{isVirtual ? ` · ${fmtHora(fechaMostrar)} hrs` : ""}</span>
                </div>

                {isVirtual && (() => {
                  const d = difDias(hoyStr(), n.slot.fecha_audiencia);
                  return (
                    <div className="lxp-ea-agbox" style={{ marginTop: 12 }}>
                      <div className="l">Pendiente de celebrarse</div>
                      <div style={{ fontSize: 12.5, color: "#475569" }}>
                        {d < 0 ? `Debió celebrarse hace ${Math.abs(d)} días.` : d === 0 ? "Es hoy." : `Faltan ${d} días.`}
                      </div>
                      <button
                        className="lxp-ea-btn pri sm"
                        style={{ marginTop: 9 }}
                        onClick={(e) => { triggerHappyWave(e); setVerNode(null); abrirRegistrarSucedido(n.claseKey, n.idx); }}
                      >
                        Registrar lo sucedido
                      </button>
                    </div>
                  );
                })()}

                {!isVirtual && n.term_dias ? (
                  <div className="lxp-ea-venc-row" style={{ marginTop: 12 }}>
                    <span className="lxp-ea-venc-chip"><i className="fa-regular fa-calendar" /> Vence {fmt(n.fecha_limite)}</span>
                    <span className="lxp-ea-venc-term">Término: {n.term_dias} días hábiles</span>
                  </div>
                ) : null}

                {!isVirtual && n.notas && <div className="lxp-ea-nota" style={{ WebkitLineClamp: "unset", marginTop: 12 }}>{n.notas}</div>}

                {!isVirtual && isAud && n.pruebasResultado?.length > 0 && (
                  <div className="lxp-ea-pdtbl-wrap" style={{ marginTop: 14 }}>
                    <div className="k" style={{ marginBottom: 6 }}>Pruebas — admisión</div>
                    <table className="lxp-ea-pdtbl">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Parte</th>
                          <th>Prueba</th>
                          <th>Detalle</th>
                          <th>Resultado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {n.pruebasResultado.map((p, pIdx) => {
                          const tipoLbl = TIPO_PRUEBA_DEMANDADA_OPTS.find((o) => o.value === p.tipo)?.label || p.tipo;
                          const resumen = p.resumen || resumenPruebaDemandada(p);
                          const admitida = p.resultado === "admitida";
                          const esActora = p.parte === "actora";
                          return (
                            <tr key={pIdx}>
                              <td>{pIdx + 1}</td>
                              <td>{esActora ? "Actora" : "Demandada"}</td>
                              <td><b>{tipoLbl}</b></td>
                              <td className="m">{resumen || "—"}</td>
                              <td>
                                <span className={`lxp-ea-st ${admitida ? "celebrada" : "sin-conciliacion"}`}>
                                  {admitida ? "Admitida" : "Desechada"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {!isVirtual && docs.length > 0 && (
                  <>
                    <div className="k" style={{ marginTop: 14, marginBottom: 6 }}>Documentos</div>
                    <div className="lxp-ea-doclist" style={{ display: "block" }}>
                      {docs.map((doc) => (
                        <div key={doc.id} className="lxp-ea-docrow" onClick={() => abrirDoc(doc)}>
                          <span className="lxp-ea-doclabel">{ROL[doc.rol] || "Doc"}</span>
                          <span className="lxp-ea-docname">{doc.filename}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {!isVirtual && esConvEvento && (
                  <div style={{ marginTop: 14 }}>
                    <ConvenioBox term={terminacion} onConstancia={abrirConstancia} onVerDoc={abrirDoc} onIrATerminacion={onIrATerminacion} />
                  </div>
                )}
              </div>
              <div className="lxp-ea-m-f">
                <span className="lxp-ea-spacer" />
                {isTerm ? (
                  onIrATerminacion && (
                    <button className="lxp-ea-btn pri" onClick={() => { setVerNode(null); onIrATerminacion(); }}>Ver / editar</button>
                  )
                ) : isVirtual ? null : isAud ? (
                  <button className="lxp-ea-btn pri" onClick={() => { setVerNode(null); editarAudienciaSucedido(n); }}>Editar</button>
                ) : !n.isDemandaAuto ? (
                  <button className="lxp-ea-btn pri" onClick={() => { setVerNode(null); editarActuacion(n); }}>Editar</button>
                ) : null}
                <button className="lxp-ea-btn" onClick={() => setVerNode(null)}>Cerrar</button>
              </div>
            </>
          );
        })()}
      </Overlay>

      {/* ── Visor de documento ── */}
      <DocPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
}

// ── Caja de convenio en el timeline ──
function ConvenioBox({ term, onConstancia, onVerDoc, onIrATerminacion }) {
  if (!term) {
    return (
      <div className="lxp-ea-cvbox tl">
        <div className="l">Convenio</div>
        <div style={{ fontSize: 12.5, color: "var(--lxp-ea-muted)" }}>
          Aún no se capturan los datos del convenio.
          {onIrATerminacion && (
            <> <button className="lxp-ea-btn lxp-ea-ghost sm" onClick={onIrATerminacion}>Abrir en Terminación</button></>
          )}
        </div>
      </div>
    );
  }
  const esParc = Number(term.tipo_pago) === 2;
  const parc = term.parcialidades || [];
  const ultimo = esParc
    ? [...parc].map((p) => p.fecha_pago).filter(Boolean).sort().pop()
    : term.fecha_pago ? String(term.fecha_pago).slice(0, 10) : null;
  const vencido = ultimo && !term.is_constancia && ultimo < dayjs().format("YYYY-MM-DD");
  return (
    <div className="lxp-ea-cvbox tl">
      <div className="l">Convenio</div>
      <div className="lxp-ea-cvg">
        <div className="lxp-ea-fl"><div className="k">Monto total</div><div className="v">{mny(term.monto_total)}</div></div>
        <div className="lxp-ea-fl"><div className="k">Forma de pago</div>
          <div className="v">{esParc ? `Parcialidades (${parc.length})` : "Una exhibición"}</div></div>
      </div>
      <div className="lxp-ea-cvf">
        {esParc
          ? parc.map((p, i) => <div key={i}>{i + 1}. {fmt(p.fecha_pago)} — {mny(p.monto)}</div>)
          : <div>{fmt(term.fecha_pago)}</div>}
      </div>
      {term.observaciones && <div className="lxp-ea-nota">{term.observaciones}</div>}
      {term.is_constancia ? (
        <div className="lxp-ea-cvok">
          ✓ Constancia de cumplimiento del {fmt(term.fecha_cumplimiento)}
          {term.filename && (
            <>
              {" · "}
              <span className="lxp-ea-doc" style={{ display: "inline-flex" }}
                onClick={() => onVerDoc({ url: term.constancia_url || `/uploads/tribunal/${term.id_tribunal || ""}/${term.path}`, filename: term.filename })}>
                <span className="rl aut">Auto</span><span className="nm">{term.filename}</span>
              </span>
            </>
          )}
        </div>
      ) : (
        <>
          {vencido && (
            <div className="lxp-ea-cvwarn">
              Venció el último pago ({fmt(ultimo)}) y el tribunal aún no emite la constancia de cumplimiento.
            </div>
          )}
          <button className="lxp-ea-btn pri sm" style={{ marginTop: 9 }} onClick={(e) => { triggerHappyWave(e); onConstancia(); }}>
            Registrar constancia de cumplimiento
          </button>
        </>
      )}
      {onIrATerminacion && (
        <button className="lxp-ea-btn lxp-ea-ghost sm" style={{ marginTop: 9, marginLeft: 8 }} onClick={onIrATerminacion}>
          Editar en Terminación
        </button>
      )}
    </div>
  );
}
